// Schedule (/scheduler/:projectId) — THE authoring surface for a project's
// coordination schedule (2026-08-18: the Plan Builder was folded in here;
// /plan-builder/:id redirects). MS-Project style: indented activity rows
// with type (task / milestone / meeting / sign-off / area release),
// durations, finish-to-start predecessors (entered as row numbers, held
// as stable row keys so re-ordering never repoints them), pinned starts,
// responsible partner + person, discipline / area / level / grid, and
// auto-computed dates (helpers/schedule/autoSchedule.js, weekend +
// holiday aware). Templates insert dated activities into the draft; the
// live gantt below is drag-editable (move / resize writes back into the
// draft rows, the engine re-runs). One Save writes the EXISTING gantt-item
// endpoints (bulk create, PATCH fields + parentId + dependencies, delete
// removed) so the result is ordinary schedule activities: issues tag to
// them, schedule health measures them, Dashboard & Schedule shows them.
import { partnerTradeIndex } from "components/Partners/partnerTrades";
import { TradeWithPartners } from "components/Partners/PartnerTradeLabel";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { renderPeopleOption, peopleTagPlaceholder } from "components/PeoplePicker/PeoplePicker";
import { peoplePickerOptions, flatOptions, groupLabel, userOrEmailRef } from "components/PeoplePicker/peoplePickerOptions";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  Alert, Badge, Button, Card, Checkbox, Dropdown, Input, InputNumber, Modal, Popconfirm, Radio, Select,
  Space, Table, Tag, Tooltip, TreeSelect, message,
} from "antd";
import { useSelector } from "react-redux";
import {
  ArrowLeftOutlined, ArrowRightOutlined, DeleteOutlined, DownOutlined,
  PlusOutlined, SaveOutlined, UpOutlined, ThunderboltOutlined, TeamOutlined,
  FileSearchOutlined, UsergroupAddOutlined, PrinterOutlined,
  ShareAltOutlined, CaretDownOutlined, CaretRightOutlined, MoreOutlined,
  CopyOutlined,
  ColumnWidthOutlined,
} from "@ant-design/icons";
import {
  moveToSection, moveRelative, moveStep, canMoveStep, splitPeriod, visibleUnderCollapse, collapsedForLevel,
  subtreeKeys, ancestorKeys, subtreeEnd, hasChildrenAt, shiftWorkingDays, assignSortIndexes, restoreSummaryEnds,
  cloneSubtree,
  redistribute,
  snapshotFromItem, changedFields, runPool,
} from "./scheduleEditOps";
import { url, tokenRef } from "api";
import { computeSchedule, durationFromDates } from "helpers/schedule/autoSchedule";
import DateRangeFilter from "components/Coordination/DateRangeFilter";
import { inDayRange, rangeEmpty } from "helpers/dateRange";
import { ACTIVITY_TYPES, ZERO_DURATION_TYPES, SCHEDULE_TEMPLATES, isoWeeksFrom, buildTemplateRows } from "helpers/schedule/templates";
import { predsToText, textToPreds, toEngineRows, removeRow, fromApiDependencies, toApiDependencies, depId } from "helpers/schedule/predecessors";
import { normalizeType, isMilestone } from "helpers/schedule/activityTypes";
import { getTaxonomy, asOptions } from "api/taxonomy/taxonomyApi";
import { getDiscipline } from "api/discipline/getDiscipline";
import { listAreaRows } from "api/areas/areasApi";
import { listCalendars, setDefaultCalendar, toEngineOptions, describeCalendar } from "api/calendars/calendarsApi";
import { importXer, downloadXer } from "api/p6/p6Api";
import FieldProgressByCategory from "components/Coordination/FieldProgressByCategory";
import SystemTasksBuilder from "components/Coordination/SystemTasksBuilder";
import ScheduleSimulator from "components/Coordination/ScheduleSimulator";
import { listBaselines, takeBaseline, activateBaseline, getVariance, slipLabel } from "api/baselines/baselinesApi";
import CoordinationGantt from "components/CoordinationGantt/CoordinationGantt";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import ReportingCatchUp from "components/Coordination/ReportingCatchUp";
import { flattenTree, gateTag, SCHEDULE_STEPS, stepLabel } from "components/CoordinationGantt/ganttHelpers";
import { getGanttFeed, deleteGanttItem, signOffGanttItem, withdrawSignOffGanttItem, updateGanttItem } from "api/gantt/ganttApi";
import ScheduleOffsetDialog from "./ScheduleOffsetDialog";
import { getParticipantsRoster } from "api/participants/participantsApi";
import useProjectAccess from "hooks/useProjectAccess";
import ShareResourceModal from "components/Share/ShareResourceModal";
import ContractorMappingModal from "components/Coordination/ContractorMappingModal";
import { rememberProject } from "helpers/projectSelection";
import { usePreference } from "helpers/userPrefs";
import { STATUS_OPTIONS, statusPatch, signOffPatch, planBulk, describeBulk, statusCascade } from "./statusEditOps";
import { placeAfter, placeBefore, datePatch, deleteActivity } from "./ganttEditOps";
import { matchOption } from "components/SearchSelect/searchSelect";

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

let keySeq = 1;
const newRow = (patch = {}) => ({
  key: `r${keySeq++}`,
  name: "", indent: 0, type: "task", durationDays: 5, manualStart: "", manualEnd: "",
  // Predecessors are the KEYS of the rows this one follows - never row
  // numbers. The table shows the number, which is only the row's current
  // position, so inserting / moving / deleting rows renumbers the display
  // without moving anybody's logic.
  predecessors: [],
  discipline: "", areaId: null, areaKey: "", level: "", grid: "", calendarId: null,
  // What the row means (a milestone's kind), what KIND of work it is, and
  // which project stage it belongs to - the three axes that turn one
  // schedule into the area / trade / stage / commissioning views.
  kind: "", category: "", stage: "",
  responsibleCompanyId: undefined, responsibleUserId: undefined, recurrence: undefined,
  // Several responsible people per activity: [{ value: userId | "email:<addr>", name }]
  responsibleUsers: [],
  contractorLabel: "",
  disabled: false,
  // Sign-off: the planned date ("" = the activity needs none) and, for a
  // saved row, the sign-off already recorded on it.
  signOffDate: "", signedOffAt: null, signedOffBy: null,
  // Where the sign-off was FIRST planned once it has moved ("" = never
  // moved): the grey star on the Gantt. Display only - the server keeps
  // its own copy of the same rule on save.
  signOffDateOriginal: "",
  // BIM-to-Field structure: which chain (bim / field / management), the
  // step in it, and the work package code shared by both chains. `gate`
  // and `earlyStart` are read-only here (computed / set by their own PATCH).
  phase: "", step: "", workPackage: "", earlyStart: null, gate: null,
  // What the FIELD says (utils/trackingProgress): counted from tracked
  // model elements, never typed here. Issues track BIM, construction
  // tracking tracks the field, checklists track management (owner).
  trackingProgress: null, trackingProgressCount: 0,
  ...patch,
});
const isZeroDur = (r) => ZERO_DURATION_TYPES.includes(r.type);
const SUMMARY_DATES_TIP = "Summary row: its duration is worked out from the rows under it. Set its start or finish to stretch the group beyond them.";
// THE RULE, SAID UP FRONT. Owner, 2026-10-01, and the leader's point that it
// belongs in the tooltip rather than being discovered by a refusal.
const SUMMARY_FLOOR_TIP = "A group's dates can be set, but only OUTWARDS: it cannot start later than its earliest activity or finish earlier than its latest. Leave it blank to go back to rolling up from the rows underneath.";
// Deepest indent the Indent button allows. Restructured schedules run
// 6-7 levels (head > area > zone > package > activity ...).
const MAX_INDENT = 9;

// How many areas read off a sheet are waiting for the owner to
// validate. Shown on the Areas button so the schedule page says there
// is something to decide without anyone having to go looking.
// The company's trade list and the project's approved areas - the two
// dictionaries the schedule used to make people re-type by hand. Both
// fail quiet: a picker with no options is a worse day than a page that
// will not load, and both columns still accept whatever is already on
// the row.
// A discipline with the partners who carry it beneath (owner, 2026-10-16).
// Searched by `text` (name + partners); the closed box shows the name only.
function disciplineOptionsWithPartners(disciplines, ix) {
  return (disciplines || []).map((d) => {
    const value = d.key || d.value;
    const name = d.value || d.key;
    const partners = ix.partnersOf(d.key, d.value);
    return { value, name, text: [name, d.key, ...partners].filter(Boolean).join(" "), label: <TradeWithPartners label={name} partners={partners} /> };
  });
}

function useDisciplines(companyId) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!companyId) { setRows([]); return; }
    let alive = true;
    getDiscipline(companyId)
      .then((r) => { if (alive) setRows(Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : [])); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [companyId]);
  return rows;
}

function useAreas(projectId) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!projectId) { setRows([]); return; }
    let alive = true;
    // Approved only: a pending area is a proposal, and filing live work
    // under one would give it authority nobody has granted yet.
    listAreaRows(projectId, "approved")
      .then((r) => { if (alive) setRows(r); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [projectId]);
  return rows;
}

// LEVELS & AREAS (owner, 2026-10-10): the schedule's levels and areas come
// from the Levels & Areas page. `levelsSource` is "levels-areas" when the
// project has any; otherwise the older controls stay (a free level, the area
// tree) - nothing is taken away from a project that has not set it up.
function useLevelsAreas(projectId) {
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    if (!projectId) { setOpts(null); return undefined; }
    let alive = true;
    const t = localStorage.getItem(tokenRef);
    fetch(`${url}/projects/${projectId}/areas/location-options?light=1`, { headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setOpts(d); })
      .catch(() => { if (alive) setOpts(null); });
    return () => { alive = false; };
  }, [projectId]);
  return opts;
}

// Areas as antd TreeSelect nodes: Building -> Level -> Zone -> Room.
// A parent whose own parent is missing becomes a root rather than
// disappearing with it.
function areaTreeData(areas) {
  // Name + level: several levels carry an "Area A", and the picker has to
  // tell them apart without opening the tree.
  const nodes = (areas || []).map((a) => ({
    value: String(a._id),
    title: `${a.code ? `${a.code} · ` : ""}${a.name}${a.level && a.level !== a.name ? ` · ${a.level}` : ""}`,
    children: [],
  }));
  const byId = new Map(nodes.map((n, i) => [n.value, { node: n, row: areas[i] }]));
  const roots = [];
  for (const { node, row } of byId.values()) {
    const parent = row.parentId ? byId.get(String(row.parentId)) : null;
    if (parent && parent.node !== node) parent.node.children.push(node);
    else roots.push(node);
  }
  return roots;
}

// A resizable header cell for the schedule table. The Gantt already
// resizes its own columns this way (a 6px grab strip, widths kept in
// localStorage); doing the same here rather than pulling in
// react-resizable, which would mean an npm install on prod for one
// drag handle.
function ResizableTitle({ onResize, width, children, ...rest }) {
  if (!width) return <th {...rest}>{children}</th>;
  return (
    <th {...rest} style={{ ...(rest.style || {}), position: "relative" }}>
      {children}
      <span
        onMouseDown={(e) => onResize(e)}
        onClick={(e) => e.stopPropagation()}
        title="Drag to resize"
        style={{
          position: "absolute", right: 0, top: 0, bottom: 0, width: 8,
          cursor: "col-resize", userSelect: "none", touchAction: "none",
        }}
      />
    </th>
  );
}

// The value, held back until it has stopped changing for `ms`. The draft
// chart re-lays out every bar on each change, and with 600+ rows that was
// the cost of every keystroke in a name box.
function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function usePendingAreaCount(projectId) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!projectId) { setCount(0); return; }
    let alive = true;
    fetch(`${url}/projects/${projectId}/areas/pending`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : { count: 0 }))
      .then((d) => { if (alive) setCount(Number(d.count || 0)); })
      .catch(() => { if (alive) setCount(0); });
    return () => { alive = false; };
  }, [projectId]);
  return count;
}


// One partner's people for a schedule row (one responsible company, many
// people), from the shared picker (components/PeoplePicker): a user id, or
// `email:<addr>` for a roster contact - saving gives them an account, as
// before (routes/ganttItems fillResponsibleUsers).
function schedulePeopleOf(group) {
  return flatOptions(peoplePickerOptions(group ? [group] : [], {
    personValue: userOrEmailRef,
    executives: "disable",
    note: (p) => (p.hasAccount ? "" : "account created on save"),
  })).map((o) => ({ ...o, name: (o.person && o.person.name) || o.label }));
}

export default function SchedulerPage() {
  const { projectId } = useParams();
  const pendingAreas = usePendingAreaCount(projectId);
  const [areasByItem, setAreasByItem] = useState({});
  const history = useHistory();
  // Schedule mode is owner-company only (ganttAdd / ganttEdit /
  // ganttDelete): partner companies never see it - anyone without
  // plan-edit rights is sent back to the read-only dashboard.
  const { can, canEditPlan, loading: accessLoading, access } = useProjectAccess(projectId);
  useEffect(() => {
    if (accessLoading || !access) return;
    if (!canEditPlan) {
      message.info("The schedule is view-only for your company on this project.");
      // An emailed /scheduler/<id>?focus=<activity> keeps its activity: the
      // dashboard opens the same row (it reads ?focus= too).
      let focus = "";
      try { focus = new URLSearchParams(window.location.search).get("focus") || ""; } catch (e) { focus = ""; }
      history.replace(focus ? `/coordination/${projectId}?focus=${encodeURIComponent(focus)}` : `/coordination?projectId=${projectId}`);
    }
  }, [accessLoading, access, canEditPlan, projectId]); // eslint-disable-line
  // Hand the plan to somebody who has no account. What THEY see is
  // decided per recipient - the rows their own company is responsible
  // for - by GET /shared/schedule/:token, not by anything chosen here.
  const [shareOpen, setShareOpen] = useState(false);
  const [projectStart, setProjectStart] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState(() => {
    const rs = [
      newRow({ name: "Phase 1", indent: 0 }),
      newRow({ name: "Task A", indent: 1 }),
      newRow({ name: "Task B", indent: 1 }),
      newRow({ name: "Phase 1 complete", indent: 1, type: "milestone" }),
    ];
    rs[2].predecessors = [rs[1].key];
    rs[3].predecessors = [rs[2].key];
    return rs;
  });
  const [projectTitle, setProjectTitle] = useState("");
  // Which field activity's element breakdown is open (read-only).
  const [fieldProgressRow, setFieldProgressRow] = useState(null);
  // Drives the virtualised table's body height (see tableScroll).
  const [viewportH, setViewportH] = useState(() => (typeof window !== "undefined" ? window.innerHeight : 900));
  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Search box: filters the editor table and the draft chart together.
  const [search, setSearch] = useState("");
  // "Today": rows running today (start <= today <= finish) or overdue and
  // not done - table and draft chart together.
  const [todayOnly, setTodayOnly] = useState(false);
  // Date ranges over the COMPUTED dates - "what starts in September",
  // "what has to finish before the pour". Computed rather than typed,
  // because a row's real start can come from its predecessors and the
  // calendar rather than from anything anyone entered: filtering on the
  // manual value would hide exactly the rows the schedule moved.
  const [startRange, setStartRange] = useState({ from: "", to: "" });
  const [endRange, setEndRange] = useState({ from: "", to: "" });
  // Assign a partner / responsible people to every row shown (search /
  // Today filtered) - a draft change, saved with the schedule.
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCompany, setAssignCompany] = useState(undefined);
  const [assignPeople, setAssignPeople] = useState([]);
  const [assignReplace, setAssignReplace] = useState(true);
  // Text typed into a row's contractor picker (offers "use as label").
  const [labelDraft, setLabelDraft] = useState({});
  // Raw text being typed into a row's Preds box, so a half-typed "2," is
  // not rewritten under the caret while the keys behind it are committed.
  const [predDraft, setPredDraft] = useState({});
  // ?focus=<gantt item id> (pen icon on the dashboard schedule): once the
  // saved schedule is loaded, that row is highlighted, scrolled into view
  // and its name field focused.
  const location = useLocation();
  const focusItemId = (() => { try { return new URLSearchParams(location.search).get("focus") || ""; } catch (e) { return ""; } })();
  const [focusRowKey, setFocusRowKey] = useState(null);
  const [roster, setRoster] = useState([]);
  // The company's own vocabularies (back/routes/taxonomy.js): stages,
  // activity categories and milestone kinds, seeded at registration and
  // editable per company - so these pickers are never empty and never a
  // free-text box that only matches itself.
  const [taxonomy, setTaxonomy] = useState({ project_stage: [], activity_category: [], milestone_kind: [], wbs_node: [] });
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateKey, setTemplateKey] = useState("weekly");
  // "Build from project levels, areas & systems" - the server-side build
  // with its own readiness check, preview and undo (SystemTasksBuilder).
  const [systemTasksOpen, setSystemTasksOpen] = useState(false);
  // "Simulate a schedule" from documents (ScheduleSimulator, owner 2026-10-16).
  const [simulateOpen, setSimulateOpen] = useState(false);
  const [anchorDate, setAnchorDate] = useState(() => isoWeeksFrom("", 0));
  const [saving, setSaving] = useState(false);
  // Existing plan items (for "Load current plan" / update-on-save).
  const [existingItems, setExistingItems] = useState([]);
  const [loadedFromPlan, setLoadedFromPlan] = useState(false);
  // What each saved activity held when it was loaded (by _id), in the
  // PATCH body's shape: Save sends only the rows that differ from it.
  const snapshotRef = useRef(new Map());
  // Collapsed summary rows (by key). View only - hidden rows stay in the
  // draft and are saved like any other.
  const [collapsed, setCollapsed] = useState(() => new Set());
  // The activity whose dates are being moved, with everything under it
  // (owner, 2026-10-05). Its own dialog because it PREVIEWS server-side
  // before it writes, which the draft-row editing on this page does not.
  const [offsetFor, setOffsetFor] = useState(null);
  // Multi-select (row keys) for the bulk actions.
  const [selectedKeys, setSelectedKeys] = useState([]);
  // Move dialog: { keys, mode: "section" | "relative" }.
  const [moveDlg, setMoveDlg] = useState(null);
  const [moveTarget, setMoveTarget] = useState(undefined);
  const [moveWhere, setMoveWhere] = useState("below");
  // Bulk field dialog: { kind: "area" | "phase" | "step" | "package" | "shift" | "responsible" }.
  const [bulkDlg, setBulkDlg] = useState(null);
  // { key, segments: [{key, start, end, days}] } while the split preview is open.
  const [splitDlg, setSplitDlg] = useState(null);
  const [bulkValue, setBulkValue] = useState(undefined);
  const [bulkShiftSignOff, setBulkShiftSignOff] = useState(true);
  // "Saving 37 of 120…" while the PATCHes run.
  const [saveProgress, setSaveProgress] = useState(null);

  // Working days (Mon–Fri) between two dates, inclusive — durations in
  // the engine count working days.
  // Counted by the engine's own rules (row calendar + company holidays), so
  // loading and saving an untouched row never moves its finish.
  const calRef = useRef({ holidays: [], engine: {} });
  const workingDaysBetween = (a, b, calendarId = null) =>
    durationFromDates(a, b, calRef.current.holidays, calRef.current.engine, calendarId);
  const isoDay = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");
  // Existing plan → editor rows in tree order: indent from depth,
  // predecessors from dependencies (resolved to row keys in a second pass,
  // once every row exists and has one), duration from dates, manual start
  // where there are no predecessors so dates stay put.
  const rowsFromPlan = (items) => {
    const flat = flattenTree(items || []).filter((r) => r.item);
    const idxById = new Map(flat.map((r, i) => [String(r.item._id), i]));
    const built = flat.map(({ item, depth }) => {
      const preds = (item.dependencies || []).filter((d) => idxById.has(depId(d)));
      const start = isoDay(item.startDate), end = isoDay(item.endDate || item.startDate);
      const type = normalizeType(item.type, item.kind).type;
      return newRow({
        _id: item._id, name: item.name || "", indent: depth, type,
        durationDays: ZERO_DURATION_TYPES.includes(type) ? 0 : workingDaysBetween(start, end, item.calendarId || null),
        // The dates it was loaded with: until the duration or start is edited,
        // its duration is re-counted when holidays / calendars arrive.
        _loadedStart: start, _loadedEnd: end,
        manualStart: preds.length ? "" : start,
        // manualEnd is filled in BELOW, for summaries only, by comparing the
        // stored span with the children's. It cannot be decided here because
        // the children's dates are not all known yet at this point in the map.
        manualEnd: "",
        predecessors: [],
        discipline: item.discipline || "", areaId: item.areaId ? String(item.areaId) : null,
        calendarId: item.calendarId ? String(item.calendarId) : null,
        areaKey: item.areaKey || "", level: item.level || "", grid: item.grid || "",
        kind: normalizeType(item.type, item.kind).kind || item.milestoneKind || "",
        category: item.category || "", stage: item.stage || "",
        responsibleCompanyId: item.responsibleCompanyId || undefined,
        responsibleUserId: item.responsibleUserId || undefined,
        responsibleUsers: (item.responsibleUsers && item.responsibleUsers.length
          ? item.responsibleUsers.map((u) => ({ value: String(u.userId), name: u.name || "" }))
          : (item.responsibleUserId ? [{ value: String(item.responsibleUserId), name: item.responsibleUserName || "" }] : [])),
        contractorLabel: item.contractorLabel || "",
        disabled: !!item.disabled,
        signOffDate: isoDay(item.signOffDate), signedOffAt: item.signedOffAt || null, signedOffBy: item.signedOffBy || null,
        signOffDateOriginal: isoDay(item.signOffDateOriginal),
        // The owner/GC sign-off (blue star) - read only, from the server.
        ownerSignOff: item.ownerSignOff || null,
        recurrence: item.recurrence || undefined,
        phase: item.phase || "", step: item.step || "", workPackage: item.workPackage || "",
        earlyStart: item.earlyStart || null, gate: item.gate || null,
        trackingProgress: item.trackingProgress === undefined ? null : item.trackingProgress,
        trackingProgressCount: item.trackingProgressCount || 0,
        progress: item.progress, status: item.status,
      });
    });
    // Second pass: dependency ids -> the key of the row that carries them.
    const keyById = new Map(flat.map((r, i) => [String(r.item._id), built[i].key]));
    flat.forEach(({ item }, i) => {
      built[i].predecessors = fromApiDependencies(item.dependencies, keyById)
        .filter((l) => l.key !== built[i].key);
    });
    // The "BELOW" promised above: a group's saved finish beyond its children
    // is its pin (scheduleEditOps.restoreSummaryEnds).
    return restoreSummaryEnds(built);
  };
  const loadCurrentPlan = (items) => {
    const list = items || existingItems;
    if (!list.length) return message.info("This project has no schedule yet");
    const rs = rowsFromPlan(list);
    snapshotRef.current = new Map(list.map((it) => [String(it._id), snapshotFromItem(it)]));
    setRows(rs);
    setSelectedKeys([]);
    // A big schedule opens folded to its heads and their direct children
    // (BIM / Field / Management -> areas); a small one opens flat.
    setCollapsed(rs.length > 150 ? collapsedForLevel(rs, 1) : new Set());
    const earliest = list.map((i) => isoDay(i.startDate)).filter(Boolean).sort()[0];
    if (earliest) setProjectStart(earliest);
    setLoadedFromPlan(true);
    message.success(`${rs.length} schedule activit${rs.length === 1 ? "y" : "ies"} loaded — edit and Save to update them`);
  };
  useEffect(() => {
    getGanttFeed(projectId)
      .then((res) => {
        const items = res?.items || [];
        setExistingItems(items);
        // The areas each row sits in ride along on the same feed, so the
        // schedule can show WHERE a row is without another request.
        setAreasByItem(res?.areasByItem || {});
        // A project with a schedule opens it for editing instead of the demo rows.
        if (items.length) loadCurrentPlan(items);
      })
      .catch(() => {});
    getParticipantsRoster(projectId)
      .then((res) => setRoster(Array.isArray(res) ? res : []))
      .catch(() => {});
    getTaxonomy().then(setTaxonomy);
    fetch(`${url}/projects/picker`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : {}))
      .then((b) => { const p = (b?.projects || []).find((x) => x._id === projectId); if (p) setProjectTitle(p.title); })
      .catch(() => {});
  }, [projectId]); // eslint-disable-line

  useEffect(() => {
    if (!focusItemId || !loadedFromPlan) return;
    const row = rows.find((r) => String(r._id || "") === String(focusItemId));
    if (!row) return;
    setSearch("");
    setFocusRowKey(row.key);
    // Unfold whatever the row sits under, or there is nothing to scroll to.
    const up = ancestorKeys(rows, row.key);
    if (up.length) setCollapsed((c) => { const n = new Set(c); up.forEach((k) => n.delete(k)); return n; });
    // Let the table render the row, then bring it into view and focus its name.
    const t = setTimeout(() => {
      const tr = document.querySelector(`tr[data-row-key="${row.key}"]`);
      if (!tr) return;
      tr.scrollIntoView({ block: "center", behavior: "smooth" });
      const input = tr.querySelector('input[placeholder="Task name"]');
      if (input) { input.focus(); input.select && input.select(); }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusItemId, loadedFromPlan]);

  // Company holiday calendar — non-working days beside weekends.
  // Everyone reads; admins/companyAdmins edit (server-enforced too).
  const userInfoRedux = useSelector((st) => st.user.userInfo);
  const canEditHolidays = !!(userInfoRedux?.admin || userInfoRedux?.companyAdmin);
  // The two dictionaries the Discipline and Area columns pick from.
  const disciplines = useDisciplines(userInfoRedux?.company);
  const areas = useAreas(projectId);
  const areaTree = useMemo(() => areaTreeData(areas), [areas]);
  const la = useLevelsAreas(projectId);
  const laOn = !!(la && la.levelsSource === "levels-areas");
  // Areas grouped under their level; a zone reused on several levels is
  // offered once per level, so picking it says which level.
  const laAreaByValue = useMemo(() => new Map(((la && la.areas) || []).map((a) => [a.value, a])), [la]);
  const laAreaOptions = useMemo(() => ((la && la.levels) || []).map((l) => ({
    label: l.label,
    options: ((la && la.areas) || []).filter((a) => a.level === l.value).map((a) => ({ value: a.value, label: `${a.label} · ${l.label}` })),
  })).filter((g) => g.options.length), [la]);
  const laLevelOptions = useMemo(() => ((la && la.levels) || []).map((l) => ({ value: l.value, label: l.label })), [la]);
  const laValueFor = (r) => {
    if (!r.areaId) return undefined;
    const list = (la && la.areas) || [];
    const hit = list.find((a) => a.areaId === String(r.areaId) && a.level === r.level)
      || list.find((a) => a.areaId === String(r.areaId));
    return hit ? hit.value : `missing:${r.areaId}`;
  };
  const laPatchFor = (v) => {
    const a = v ? laAreaByValue.get(v) : null;
    return a ? { areaId: a.areaId, areaKey: a.label, level: a.level } : { areaId: null };
  };
  const areaNameById = useMemo(
    () => new Map((areas || []).map((a) => [String(a._id), `${a.code ? `${a.code} · ` : ""}${a.name}`])),
    [areas]
  );
  // Arriving here (deep link, breadcrumb, landing) selects this project for
  // the whole coordination suite - remember it.
  useEffect(() => { if (projectId) rememberProject(userInfoRedux?._id || userInfoRedux?.id, projectId); }, [projectId]); // eslint-disable-line
  const [holidays, setHolidays] = useState([]);
  // Dates are laid out on the calendars and the company holidays. Saving
  // before both have loaded would store dates computed without them - and
  // the next load would move them back - so Save waits for both.
  const [calReady, setCalReady] = useState({ holidays: false, calendars: false });
  const calendarsReady = calReady.holidays && calReady.calendars;
  // Which days each crew works. The first read seeds 5x8 / 6x10 / 7x24.
  const [calendars, setCalendars] = useState([]);
  const reloadCalendars = React.useCallback(() => {
    if (!projectId) return;
    listCalendars(projectId).then((rows) => setCalendars(Array.isArray(rows) ? rows : [])).catch(() => setCalendars([]))
      .finally(() => setCalReady((r) => ({ ...r, calendars: true })));
  }, [projectId]);
  useEffect(() => { reloadCalendars(); }, [reloadCalendars]);
  const calendarOptions = useMemo(
    () => (calendars || []).map((c) => ({
      value: String(c._id),
      label: `${c.name}${c.isDefault ? " (default)" : ""}`,
      title: describeCalendar(c),
    })),
    [calendars]
  );
  const engineCalendars = useMemo(() => toEngineOptions(calendars), [calendars]);
  const [holidayModal, setHolidayModal] = useState(false);
  const [contractorModal, setContractorModal] = useState(false);
  const [templateAreas, setTemplateAreas] = useState([]);
  // Column widths, remembered per person (helpers/userPrefs). Defaults are
  // tuned so the table fits a normal screen without a horizontal scrollbar;
  // anyone who wants a wider Name or Responsible can drag it and it sticks.
  const [savedColW, setColW] = usePreference("scheduler.colWidths", {}, { legacyKey: "scheduler_col_widths" });
  const colW = savedColW && typeof savedColW === "object" ? savedColW : {};
  const beginColResize = (key, startW) => (e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const onMove = (ev) => {
      const w = Math.max(48, Math.min(700, startW + (ev.clientX - startX)));
      setColW((c) => ({ ...c, [key]: w }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  // Primavera round trip. The import replaces the draft wholesale, so it
  // reloads the saved plan afterwards rather than trying to merge into
  // whatever is on screen.
  // Baselines: what the plan said when someone agreed to it.
  const [baselines, setBaselines] = useState([]);
  const [variance, setVariance] = useState(null);
  const [varianceOpen, setVarianceOpen] = useState(false);
  const [baselineName, setBaselineName] = useState("");
  const reloadBaselines = React.useCallback(() => {
    if (!projectId) return;
    listBaselines(projectId).then((r) => setBaselines(Array.isArray(r) ? r : [])).catch(() => setBaselines([]));
  }, [projectId]);
  useEffect(() => { reloadBaselines(); }, [reloadBaselines]);
  const openVariance = async (id = "active") => {
    try {
      setVariance(await getVariance(projectId, id));
      setVarianceOpen(true);
    } catch (e) {
      message.info(e.status === 404 ? "No baseline has been taken yet" : (e.message || "Could not read the variance"));
    }
  };
  const xerInputRef = React.useRef(null);
  const [p6Busy, setP6Busy] = useState(false);
  const onXerPicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setP6Busy(true);
    try {
      const r = await importXer(projectId, file);
      message.success(
        `P6 import: ${r.created} new, ${r.updated} updated, ${r.links} relationships, ${r.calendars} calendars` +
        (r.skippedLinks ? ` (${r.skippedLinks} link${r.skippedLinks === 1 ? "" : "s"} pointed outside this project)` : "")
      );
      reloadCalendars();
      // Re-read the saved plan: the import wrote straight to the server,
      // so whatever is on screen is now behind.
      const res = await getGanttFeed(projectId);
      const items = res?.items || [];
      setExistingItems(items);
      setAreasByItem(res?.areasByItem || {});
      if (items.length) loadCurrentPlan(items);
    } catch (err) {
      message.error(err.message || "P6 import failed");
    } finally {
      setP6Busy(false);
    }
  };
  const [newHoliday, setNewHoliday] = useState({ date: "", label: "" });
  useEffect(() => {
    fetch(`${url}/company-settings/holidays`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : { holidays: [] }))
      .then((b) => setHolidays(b.holidays || []))
      .catch(() => {})
      .finally(() => setCalReady((r) => ({ ...r, holidays: true })));
  }, []);
  const saveHolidays = async (next) => {
    const res = await fetch(`${url}/company-settings/holidays`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify({ holidays: next }),
    });
    const b = await res.json();
    if (!res.ok) return message.error(b.err || "Holiday save failed");
    setHolidays(b.holidays || []);
    message.success("Holiday calendar saved");
  };

  // The engine works in 1-based row numbers; the editor works in stable
  // keys. Translate at this single boundary and carry the keys through as
  // `predKeys`, so the chart and the save path never see a row number.
  //
  // The engine only reads a few fields of each row (order, indent,
  // duration, pinned start, links, calendar, zero-duration type). Typing a
  // name, a package code or a level used to re-run the whole forward and
  // backward pass over every row on each keystroke; now the engine input
  // is kept by reference while none of those fields changed, and the
  // computed dates are merged back onto the live rows.
  const engineInputRef = useRef([]);
  const engineInput = useMemo(() => {
    const prev = engineInputRef.current;
    const same = prev.length === rows.length && rows.every((r, i) => {
      const p = prev[i];
      return p.key === r.key && p.indent === r.indent && p.durationDays === r.durationDays
        && p.manualStart === r.manualStart && p.manualEnd === r.manualEnd
        && p.predecessors === r.predecessors
        && p.calendarId === r.calendarId && p.type === r.type;
    });
    if (same) return prev;
    const next = rows.map((r) => ({
      // manualEnd is in BOTH halves of this memo. A field added to the
      // payload and not to the comparison above is a field whose change
      // never re-runs the engine - the pin would be stored and ignored until
      // something else happened to make the rows look different.
      key: r.key, indent: r.indent, durationDays: r.durationDays,
      manualStart: r.manualStart, manualEnd: r.manualEnd,
      predecessors: r.predecessors, calendarId: r.calendarId, type: r.type,
    }));
    engineInputRef.current = next;
    return next;
  }, [rows]);
  const engineRows = useMemo(() => toEngineRows(engineInput, isZeroDur), [engineInput]);
  // Holidays and calendars load separately from the plan: re-count every
  // row still carrying its loaded dates, so the first save does not move it.
  useEffect(() => {
    calRef.current = { holidays, engine: engineCalendars };
    setRows((rs) => {
      let changed = false;
      const next = rs.map((r) => {
        if (!r._loadedEnd || !r._loadedStart || ZERO_DURATION_TYPES.includes(r.type)) return r;
        const d = durationFromDates(r._loadedStart, r._loadedEnd, holidays, engineCalendars, r.calendarId || null);
        if (d === r.durationDays) return r;
        changed = true;
        return { ...r, durationDays: d };
      });
      return changed ? next : rs;
    });
  }, [holidays, engineCalendars]); // eslint-disable-line react-hooks/exhaustive-deps

  const engineOut = useMemo(
    () => computeSchedule(engineRows, projectStart, holidays, engineCalendars),
    [engineRows, projectStart, holidays, engineCalendars]
  );
  const computed = useMemo(() => ({
    ...engineOut,
    rows: engineOut.rows.map((c, i) => ({
      ...rows[i],
      predecessors: c.predecessors, predKeys: c.predKeys, milestone: c.milestone,
      wbs: c.wbs, isSummary: c.isSummary, parentRow: c.parentRow,
      startDate: c.startDate, endDate: c.endDate, lateStart: c.lateStart, lateFinish: c.lateFinish,
      totalFloatDays: c.totalFloatDays, critical: c.critical,
    })),
  }), [engineOut, rows]);
  // Cells look their computed row up by key - a Map, not a find() per cell.
  const computedByKey = useMemo(() => new Map(computed.rows.map((c) => [c.key, c])), [computed]);

  // Templates: dated activities relative to the anchor -> engine rows with a
  // pinned start + working-day duration, appended to the draft.
  const applyTemplate = () => {
    const tpl = SCHEDULE_TEMPLATES.find((t) => t.key === templateKey);
    if (!tpl) return;
    // Built and saved on the server, not inserted into the draft.
    if (tpl.serverBuild) {
      setTemplateModalOpen(false);
      setSystemTasksOpen(true);
      return;
    }
    if (!anchorDate) return message.warning("Pick an anchor date first");
    // A template builds a NETWORK: buildTemplateRows allocates the row
    // keys and turns each `after` ref into a real predecessor link, so
    // the sequence moves as one instead of being nailed to dates.
    const chosen = (areas || []).filter((a) => templateAreas.includes(String(a._id)))
      .map((a) => ({ key: String(a._id), name: a.name, level: a.level || "" }));
    // The backbone template reads the COMPANY's WBS list rather than a
    // hardcoded one; other templates ignore it.
    const built = buildTemplateRows(
      tpl,
      { anchor: anchorDate, areas: chosen, wbsNodes: taxonomy.wbs_node || [] },
      newRow
    );
    // The first row is what the anchor pins; everything after it is
    // scheduled from its links.
    // A SUMMARY'S PIN IS RECONSTRUCTED, NOT STORED - and the alternative was
    // a model change this does not need.
    //
    // The pin MEANS "this group's own dates reach beyond its children". That
    // is exactly what the saved dates already record: the engine writes the
    // pinned span to startDate/endDate, so on the way back in, a stored start
    // EARLIER than the children's earliest, or a stored finish LATER than
    // their latest, IS the pin. A pin inside the span is indistinguishable
    // from no pin, and behaves identically, so nothing is lost.
    //
    // AND THIS IS WHY SUMMARIES CANNOT JUST REUSE THE LINE ABOVE: that sets
    // manualStart from the loaded start for every row with no predecessor,
    // and a summary never has one - so every group in the plan would load
    // PINNED to wherever it happened to roll up to last time, and the whole
    // schedule would stop moving. Found by reading the line rather than by a
    // test, because every test builds its rows by hand.
    for (let i = 0; i < built.length; i++) {
      const isSummary = i + 1 < built.length && (built[i + 1].indent || 0) > (built[i].indent || 0);
      if (!isSummary) continue;
      const base = built[i].indent || 0;
      let earliest = null; let latest = null;
      for (let j = i + 1; j < built.length && (built[j].indent || 0) > base; j++) {
        const cs = built[j]._loadedStart; const ce = built[j]._loadedEnd;
        if (cs && (earliest == null || cs < earliest)) earliest = cs;
        if (ce && (latest == null || ce > latest)) latest = ce;
      }
      const ownStart = built[i]._loadedStart; const ownEnd = built[i]._loadedEnd;
      built[i].manualStart = ownStart && earliest && ownStart < earliest ? ownStart : "";
      built[i].manualEnd = ownEnd && latest && ownEnd > latest ? ownEnd : "";
    }
    if (built.length && !built[0].manualStart) built[0].manualStart = anchorDate;
    setRows((rs) => [...rs.filter((r) => r.name.trim() !== ""), ...built]);
    setTemplateModalOpen(false);
    const links = built.reduce((n, r) => n + (r.predecessors || []).length, 0);
    message.info(`"${tpl.name}" added — ${built.length} activities, ${links} links${chosen.length ? ` across ${chosen.length} area${chosen.length === 1 ? "" : "s"}` : ""}. Adjust, then Save schedule.`);
  };

  // Gantt drag on the draft: move pins the start (duration kept), resize
  // sets the duration; the engine recomputes everything downstream.
  const onGanttLocalChange = (gid, body) => {
    const c = computed.rows.find((x) => x.key === gid);
    if (!c) return;
    setRows((rs) => rs.map((r) => {
      if (r.key !== gid) return r;
      if (body.startDate !== undefined) {
        const patch = { manualStart: body.startDate };
        if (body.endDate !== undefined && !isZeroDur(r)) patch.durationDays = workingDaysBetween(body.startDate, body.endDate, r.calendarId || null);
        return { ...r, ...patch };
      }
      if (body.endDate !== undefined && !isZeroDur(r)) {
        return { ...r, durationDays: workingDaysBetween(c.startDate, body.endDate, r.calendarId || null), _loadedEnd: undefined };
      }
      return r;
    }));
  };

  // "BES - Electrical": the partner with its trade (components/PeoplePicker
  // groupLabel, owner 2026-10-15 - "their discipline on the partner header").
  const companyOptions = roster.map((c) => ({ value: c.companyId, label: groupLabel(c) || "Company", name: c.companyName || "" }));
  // Partners by trade and trades by partner, from the roster this page holds.
  const tradeIx = useMemo(() => partnerTradeIndex(roster), [roster]);
  const companyNamesById = useMemo(() => {
    const m = {};
    roster.forEach((c) => { if (c.companyId) m[c.companyId] = c.companyName || "Company"; });
    return m;
  }, [roster]);

  const setRow = (key, patch) =>
    setRows((rs) => rs.map((r) => {
      if (r.key !== key) return r;
      // An edited duration / start / calendar is the user's, not the loaded dates'.
      const edited = ("durationDays" in patch || "manualStart" in patch || "manualEnd" in patch || "calendarId" in patch || "type" in patch) ? { _loadedEnd: undefined } : {};
      return { ...r, ...patch, ...edited };
    }));

  // THE FLOOR A SUMMARY CANNOT CROSS, from its own children.
  //
  // Owner, 2026-10-01: "the lowest date a parent can take is the last
  // date/finish of his latest child", and symmetric at the other end - a
  // parent may not start later than its earliest child.
  //
  // Read off the COMPUTED rows, not the raw ones, because a child's dates are
  // derived and a child that is itself a summary has its own floor already in
  // them. Direct and indirect children both count: the span of everything
  // indented under this row.
  const floorFor = useCallback((key) => {
    const list = computed.rows;
    const i = list.findIndex((r) => r.key === key);
    if (i < 0) return null;
    const base = list[i].indent || 0;
    let earliest = null; let latest = null;
    for (let j = i + 1; j < list.length && (list[j].indent || 0) > base; j++) {
      const s = list[j].startDate; const e = list[j].endDate;
      if (s && (earliest == null || s < earliest)) earliest = s;
      if (e && (latest == null || e > latest)) latest = e;
    }
    return earliest || latest ? { earliest, latest } : null;
  }, [computed.rows]);

  // A pinned summary date is REFUSED AT THE INPUT, naming the date, because
  // this is the only place there is a person to tell. The engine clamps
  // silently - it has nobody to explain itself to.
  const setSummaryDate = useCallback((key, which, value) => {
    if (!value) { setRow(key, which === "start" ? { manualStart: "" } : { manualEnd: "" }); return; }
    const f = floorFor(key);
    if (f) {
      if (which === "start" && f.earliest && value > f.earliest) {
        message.warning(`"${value}" is after this group's earliest activity, which starts ${f.earliest}. A group cannot start later than the work inside it.`);
        return;
      }
      if (which === "end" && f.latest && value < f.latest) {
        message.warning(`"${value}" is before this group's latest activity, which finishes ${f.latest}. A group cannot finish earlier than the work inside it - split its period if you need it shorter.`);
        return;
      }
    }
    setRow(key, which === "start" ? { manualStart: value } : { manualEnd: value });
  }, [floorFor]);

  // ── the Gantt as an editor (owner, 2026-10-10) ─────────────────────────
  // Right-click a day on an activity's row, or drag a row in the chart's
  // list: every action below is the SAME edit the plan table makes, on the
  // same draft rows, so the result is identical and Save schedule persists
  // it the same way.
  const [ganttRename, setGanttRename] = useState(null); // {key, name}
  const [ganttPlace, setGanttPlace] = useState(null); // {key, mode:'after'|'before', target}
  // What the engine WILL put this row at, from the rows about to be set -
  // so a date a link overrides is reported instead of silently not moving.
  const startAfter = (nextRows, key) => {
    try {
      const out = computeSchedule(toEngineRows(nextRows.map((r) => ({
        key: r.key, indent: r.indent, durationDays: r.durationDays, manualStart: r.manualStart, manualEnd: r.manualEnd,
        predecessors: r.predecessors, calendarId: r.calendarId, type: r.type,
      })), isZeroDur), projectStart, holidays, engineCalendars);
      const i = nextRows.findIndex((r) => r.key === key);
      return i >= 0 && out.rows[i] ? out.rows[i] : null;
    } catch (e) { return null; }
  };
  const onGanttContext = (key, action, { date } = {}) => {
    const r = rows.find((x) => x.key === key);
    const c = computedByKey.get(key);
    if (!r) return;
    if (action === "start" || action === "finish") {
      if (c && c.isSummary) return setSummaryDate(key, action === "start" ? "start" : "end", date);
      const res = datePatch(r, c, action, date, workingDaysBetween);
      if (res.error) return message.warning(res.error);
      const next = rows.map((x) => (x.key === key ? { ...x, ...res.patch, _loadedEnd: undefined } : x));
      setRows(next);
      const got = startAfter(next, key);
      if (action === "start" && got && got.startDate && got.startDate !== date) {
        message.info(`"${r.name}" cannot start before ${got.startDate}: an activity it follows holds it there. Remove that link, or use "Place after" on another activity.`);
      }
      return;
    }
    if (action === "signoff") {
      const patch = signOffPatch(r, date);
      if (!patch) return message.warning(r.signedOffAt ? "Already signed off - withdraw the sign-off on the plan table to move its date" : (r.disabled ? "This activity is disabled" : "That is already its sign-off date"));
      setRow(key, patch);
      message.success(r.signOffDate ? `Sign-off moved to ${date}; the grey star keeps ${patch.signOffDateOriginal || r.signOffDate} - Save schedule to persist` : `Sign-off date set to ${date} - Save schedule to persist`);
      return;
    }
    if (action === "rename") return setGanttRename({ key, name: r.name });
    if (action === "after" || action === "before") return setGanttPlace({ key, mode: action, target: undefined });
    if (action === "delete") {
      const i = rows.findIndex((x) => x.key === key);
      const kids = hasChildrenAt(rows, i);
      let withChildren = false;
      Modal.confirm({
        title: `Delete "${r.name}"?`,
        content: (
          <div>
            <div style={{ color: "#666", marginBottom: 8 }}>Removed from the draft; Save schedule deletes the saved activity.</div>
            {kids ? <Checkbox onChange={(e) => { withChildren = e.target.checked; }}>Also delete everything under it</Checkbox> : null}
          </div>
        ),
        okText: "Delete", okButtonProps: { danger: true },
        onOk: () => {
          const res = deleteActivity(rows, key, withChildren);
          setRows(res.rows);
          message.success(`${res.removed} row${res.removed === 1 ? "" : "s"} removed - Save schedule to persist`);
        },
      });
    }
  };
  const applyGanttPlace = () => {
    if (!ganttPlace || !ganttPlace.target) return message.warning("Pick an activity");
    const res = (ganttPlace.mode === "before" ? placeBefore : placeAfter)(rows, ganttPlace.key, ganttPlace.target);
    if (res.error) return message.error(res.error);
    setRows(res.rows);
    setGanttPlace(null);
    message.success("Linked - Save schedule to persist");
  };
  const applyGanttRename = () => {
    const name = String((ganttRename && ganttRename.name) || "").trim();
    if (!name) return message.warning("A name is required");
    setRow(ganttRename.key, { name });
    setGanttRename(null);
  };
  // A row dragged onto another in the chart's list: the plan table's
  // "Move above / below row", with everything under it.
  const onGanttMoveRow = (fromKey, targetKey, where) => {
    const res = moveRelative(rows, [fromKey], targetKey, where);
    if (res.error) return message.error(res.error);
    setRows(res.rows);
    setCollapsed((cs) => { const n = new Set(cs); ancestorKeys(res.rows, fromKey).forEach((k) => n.delete(k)); return n; });
    message.success(`Moved ${res.moved} row${res.moved === 1 ? "" : "s"} - Save schedule to persist`);
  };
  const placeOptions = useMemo(() => {
    if (!ganttPlace) return [];
    return computed.rows.filter((c) => c.key !== ganttPlace.key && String(c.name || "").trim())
      .map((c) => ({ value: c.key, label: `${c.wbs || ""}  ${c.name}${c.startDate ? `  (${c.startDate} - ${c.endDate})` : ""}` }));
  }, [ganttPlace, computed.rows]);


  // Up / down moves the row WITH its subtree past the neighbouring sibling
  // (a leaf with no sibling that way still steps across, as before).
  const move = (key, delta) => setRows((rs) => moveStep(rs, key, delta));

  // ── sections: collapse, move, bulk ────────────────────────────────────
  const collapseTo = (level) => {
    setCollapsed(collapsedForLevel(rows, level));
  };
  const toggleCollapsed = (key) => setCollapsed((c) => {
    const n = new Set(c);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });
  const expandAllBelow = (key) => setCollapsed((c) => {
    const n = new Set(c);
    subtreeKeys(rows, key).forEach((k) => n.delete(k));
    return n;
  });
  const openMove = (keys, mode) => {
    if (!keys || !keys.length) return message.info("Select rows first");
    setMoveTarget(undefined);
    setMoveWhere("below");
    setMoveDlg({ keys, mode });
  };
  const applyMove = () => {
    if (!moveDlg) return;
    if (!moveTarget) return message.warning(moveDlg.mode === "section" ? "Pick a section" : "Pick a row");
    const res = moveDlg.mode === "section"
      ? moveToSection(rows, moveDlg.keys, moveTarget)
      : moveRelative(rows, moveDlg.keys, moveTarget, moveWhere);
    if (res.error) return message.error(res.error);
    setRows(res.rows);
    // Unfold the destination so the moved rows are in sight.
    const up = moveDlg.mode === "section"
      ? [moveTarget, ...ancestorKeys(res.rows, moveTarget)]
      : ancestorKeys(res.rows, moveTarget);
    setCollapsed((c) => { const n = new Set(c); up.forEach((k) => n.delete(k)); return n; });
    setMoveDlg(null);
    message.success(`Moved ${res.moved} row${res.moved === 1 ? "" : "s"} - Save schedule to persist`);
  };

  // A tree of every summary row (a row with children, or typed summary),
  // for the "Move to section" picker. Rows being moved - and their
  // subtrees - are disabled: nothing moves into itself.
  const sectionTree = useMemo(() => {
    if (!moveDlg || moveDlg.mode !== "section") return [];
    const moving = new Set();
    const want = new Set(moveDlg.keys);
    rows.forEach((r, i) => {
      if (want.has(r.key)) rows.slice(i, subtreeEnd(rows, i)).forEach((x) => moving.add(x.key));
    });
    const roots = [];
    const stack = [];
    rows.forEach((r, i) => {
      if (!(hasChildrenAt(rows, i) || r.type === "summary")) return;
      const c = computedByKey.get(r.key);
      const node = {
        value: r.key,
        title: `${c?.wbs ? `${c.wbs} ` : ""}${r.name || "(unnamed)"}`,
        disabled: moving.has(r.key),
        children: [],
      };
      const ind = r.indent || 0;
      while (stack.length && stack[stack.length - 1].indent >= ind) stack.pop();
      if (stack.length) stack[stack.length - 1].node.children.push(node); else roots.push(node);
      stack.push({ indent: ind, node });
    });
    const prune = (ns) => ns.forEach((n) => { if (!n.children.length) delete n.children; else prune(n.children); });
    prune(roots);
    return roots;
  }, [moveDlg, rows, computedByKey]);
  const rowPickOptions = useMemo(() => {
    if (!moveDlg || moveDlg.mode !== "relative") return [];
    return rows.map((r, i) => {
      const c = computedByKey.get(r.key);
      return { value: r.key, label: `#${i + 1}  ${c?.wbs || ""}  ${"· ".repeat(r.indent || 0)}${r.name || "(unnamed)"}` };
    });
  }, [moveDlg, rows, computedByKey]);

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  // A deleted row leaves the selection too.
  useEffect(() => {
    if (!selectedKeys.length) return;
    const have = new Set(rows.map((r) => r.key));
    if (selectedKeys.some((k) => !have.has(k))) setSelectedKeys((ks) => ks.filter((k) => have.has(k)));
  }, [rows]); // eslint-disable-line
  // SPLIT A PARENT'S PERIOD ACROSS ITS DIRECT CHILDREN (owner, 2026-09-28).
  // The arithmetic is in scheduleEditOps.splitPeriod and is deliberately
  // CALENDAR-day, with the children SHARING their boundary days - his rule,
  // not the scheduler reflex of starting the next one the following day.
  //
  // IT PREVIEWS BEFORE IT WRITES. This moves every child of a parent at
  // once; a person should see the dates before they land, not discover them
  // in the Gantt afterwards.
  const openSplit = (key) => {
    const c = computedByKey.get(key);
    const r = splitPeriod(rows, key, { start: c?.startDate, end: c?.endDate });
    if (!r.ok) return message.info(r.why);
    setSplitDlg({ key, segments: r.segments });
  };
  const applySplit = () => {
    if (!splitDlg) return;
    const hol = new Set((holidays || []).map((h) => String(h?.date || h).slice(0, 10)));
    const by = new Map(splitDlg.segments.map((sg) => [sg.key, sg]));
    let n = 0;
    setRows((rs) => rs.map((r) => {
      const sg = by.get(r.key);
      if (!sg) return r;
      n += 1;
      // THE SPLIT IS IN CALENDAR DAYS AND THE EDITOR'S DURATION IS IN
      // WORKING ONES, so the duration is converted rather than copied -
      // otherwise a child whose slice covers a weekend would finish late.
      // Baselines and forecast fields are untouched: a baseline that moves
      // with a re-plan is not a baseline.
      return { ...r, manualStart: sg.start, durationDays: Math.max(0, workingDaysBetween(sg.start, sg.end, r.calendarId || null)) };
    }));
    message.success(`Split across ${n} child${n === 1 ? "" : "ren"} - Save schedule to persist`);
    setSplitDlg(null);
  };
  const openBulk = (kind) => {
    if (!selectedKeys.length) return message.info("Select rows first");
    setBulkValue(kind === "shift" ? 5 : undefined);
    setBulkDlg({ kind });
  };
  const applyBulk = () => {
    if (!bulkDlg) return;
    const kind = bulkDlg.kind;
    const keys = selectedSet;
    const n = keys.size;
    if (kind === "shift") {
      const days = Math.round(Number(bulkValue) || 0);
      if (!days) return message.warning("Enter a number of working days (negative moves earlier)");
      // A selected summary shifts everything under it; its own dates are
      // derived, so it is the leaves that move.
      const affected = new Set();
      rows.forEach((r, i) => {
        if (keys.has(r.key)) rows.slice(i, subtreeEnd(rows, i)).forEach((x) => affected.add(x.key));
      });
      const hol = new Set((holidays || []).map((h) => String(h?.date || h).slice(0, 10)));
      let moved = 0; let linked = 0;
      setRows(rows.map((r, i) => {
        if (!affected.has(r.key)) return r;
        const patch = {};
        if (!hasChildrenAt(rows, i)) {
          const base = r.manualStart || computedByKey.get(r.key)?.startDate;
          if (base) {
            patch.manualStart = shiftWorkingDays(base, days, hol);
            moved += 1;
            if (days < 0 && (r.predecessors || []).length) linked += 1;
          }
        }
        if (bulkShiftSignOff && r.signOffDate && !r.signedOffAt) patch.signOffDate = shiftWorkingDays(r.signOffDate, days, hol);
        return Object.keys(patch).length ? { ...r, ...patch } : r;
      }));
      message.success(`Shifted ${moved} activit${moved === 1 ? "y" : "ies"} by ${days} working day${Math.abs(days) === 1 ? "" : "s"}${linked ? ` - ${linked} tied to predecessors cannot start before their links allow` : ""}. Save schedule to persist`);
      setBulkDlg(null);
      return;
    }
    // STATUS AND SIGN-OFF DATE DO NOT USE THE GENERIC PATH BELOW, and the
    // reason is worth stating: that path reports "Applied to N rows"
    // whatever it actually did. These two SKIP rows - a signed row, a
    // disabled row - so they have to say which, or a bulk edit that passed
    // over a third of the selection looks like one that worked.
    if (kind === "status" || kind === "signoff") {
      const patchOf = kind === "status"
        ? (r) => statusPatch(r, bulkValue)
        : (r) => signOffPatch(r, bulkValue);
      if (kind === "status" && !bulkValue) return message.warning("Pick a status");
      const plan = planBulk(rows, keys, patchOf);
      if (!plan.changes.length) { message.warning(`Nothing changed - ${describeBulk(plan)}`); return; }
      const byKey = new Map(plan.changes.map((c) => [c.key, c.patch]));
      setRows((rs) => rs.map((r) => (byKey.has(r.key) ? { ...r, ...byKey.get(r.key) } : r)));
      message.success(`${describeBulk(plan)} - Save schedule to persist`);
      setBulkDlg(null);
      return;
    }
    let patchFor;
    if (kind === "area" && laOn) {
      const p = laPatchFor(bulkValue || null);
      patchFor = () => p;
    } else if (kind === "area") {
      const v = bulkValue || null;
      patchFor = (r) => ({ areaId: v, areaKey: v ? (areaNameById.get(String(v)) || r.areaKey) : r.areaKey });
    } else if (kind === "phase") {
      patchFor = () => ({ phase: bulkValue || "" });
    } else if (kind === "step") {
      patchFor = () => ({ step: String(bulkValue || "").trim() });
    } else if (kind === "package") {
      patchFor = () => ({ workPackage: String(bulkValue || "").trim().toUpperCase() });
    } else if (kind === "responsible") {
      const v = bulkValue;
      patchFor = (r, isSummary) => {
        if (isSummary) return null; // summaries carry no responsible
        if (!v) return { responsibleCompanyId: undefined, responsibleUserId: undefined, responsibleUsers: [], contractorLabel: "" };
        return String(r.responsibleCompanyId || "") === String(v)
          ? {}
          : { responsibleCompanyId: v, responsibleUserId: undefined, responsibleUsers: [], contractorLabel: "" };
      };
    } else if (kind === "discipline") {
      patchFor = () => ({ discipline: String(bulkValue || "").trim() });
    } else if (kind === "level") {
      patchFor = () => ({ level: String(bulkValue || "").trim() });
    } else if (kind === "grid") {
      patchFor = () => ({ grid: String(bulkValue || "").trim() });
    } else if (kind === "type") {
      // THE SAME RULES THE ROW PICKER APPLIES, not a bare assignment: a
      // zero-duration type takes a zero duration, and `kind` only survives
      // on a milestone because a behaviour change can invalidate the
      // meaning - a task is not a Notice To Proceed. A SUMMARY IS SKIPPED
      // rather than converted: its dates are derived from the rows under
      // it, and the row picker steps aside there for the same reason.
      const v = bulkValue;
      patchFor = (r, isSummary) => {
        if (!v || isSummary || r.type === "summary") return null;
        return {
          type: v,
          durationDays: ZERO_DURATION_TYPES.includes(v) ? 0 : (r.durationDays || 1),
          kind: isMilestone(v) ? r.kind : "",
        };
      };
    } else if (kind === "name") {
      const v = String(bulkValue || "").trim();
      if (!v) return message.warning("Give a name - bulk naming cannot clear it");
      patchFor = () => ({ name: v });
    } else return;
    setRows((rs) => rs.map((r, i) => {
      if (!keys.has(r.key)) return r;
      const p = patchFor(r, hasChildrenAt(rs, i));
      return p ? { ...r, ...p } : r;
    }));
    message.success(`Applied to ${n} row${n === 1 ? "" : "s"} - Save schedule to persist`);
    setBulkDlg(null);
  };
  // DUPLICATE A STRUCTURE (owner, 2026-10-02: "a clone to a full
  // structure in the schedule, parents and their children, then edit
  // every field"). The copies land SELECTED, because the editing he
  // wants next is the Set… menu beside this button - so the two together
  // are the feature and neither half needed anything new.
  //
  // A SELECTED CHILD OF A SELECTED PARENT RIDES ALONG rather than being
  // copied twice: once a block's root is taken, `subtreeEnd` skips the
  // rest of it - the same rule the move code uses, for the same reason.
  const bulkDuplicate = () => {
    const tops = [];
    for (let i = 0; i < rows.length; i++) {
      if (!selectedSet.has(rows[i].key)) continue;
      tops.push(rows[i].key);
      i = subtreeEnd(rows, i) - 1;
    }
    if (!tops.length) return message.info("Select a row first");
    let next = rows;
    const added = [];
    tops.forEach((k) => {
      // Each clone finds its source BY KEY, so inserts shifting the
      // indexes underneath it cannot matter.
      const r = cloneSubtree(next, k, { makeKey: () => `r${keySeq++}` });
      next = r.rows;
      added.push(...r.addedKeys);
    });
    if (!added.length) return;
    setRows(next);
    // The copies, not the originals: editing them is the next thing.
    setSelectedKeys(added);
    message.success(
      `${added.length} row${added.length === 1 ? "" : "s"} copied - the copies are selected, and nothing is saved until Save schedule`,
    );
  };

  // REDISTRIBUTE: divide a group's period between its children, in
  // working days, recursively (owner, 2026-10-02).
  //
  // IT OVERRIDES HIS OWN FLOOR ON PURPOSE. setSummaryDate refuses a
  // finish earlier than the latest child; that stays for ordinary edits.
  // This writes manualStart/manualEnd directly, which is the escape
  // hatch he asked for - a parent cannot be dragged under its children by
  // accident, only through this flow.
  //
  // PREVIEW FIRST, AND ONE UNDO. He did not ask for either. This rewrites
  // every date under a row on a live schedule in one click, and he lost
  // 615 rows earlier today to an action that did exactly what it said.
  const [redist, setRedist] = useState(null);        // the preview, or null
  const [redistUndo, setRedistUndo] = useState(null); // rows before the last apply

  const openRedistribute = () => {
    if (selectedKeys.length !== 1) { message.info("Select the one group to redistribute"); return; }
    const key = selectedKeys[0];
    const r = redistribute(rows, key, { holidays: calRef.current.holidays });
    if (!r.ok) { message.warning(r.why); return; }
    if (!r.changes.length && r.refused.length) {
      message.warning(
        `${r.refused[0].workingDays} working day${r.refused[0].workingDays === 1 ? "" : "s"} cannot be divided `
        + `between ${r.refused[0].children} children - each needs at least one.`,
      );
      return;
    }
    setRedist({ key, name: (rows.find((x) => x.key === key) || {}).name || "", ...r });
  };

  const applyRedistribute = () => {
    if (!redist) return;
    const byKey = new Map(redist.changes.map((c) => [c.key, c]));
    setRedistUndo(rows);                 // ONE undo, and only the last one
    setRows((rs) => rs.map((r) => {
      const c = byKey.get(r.key);
      // _loadedEnd is kept so the save diff still knows what was loaded.
      return c ? { ...r, manualStart: c.start, manualEnd: c.end } : r;
    }));
    setRedist(null);
    message.success(
      `${redist.changes.length} activit${redist.changes.length === 1 ? "y" : "ies"} redistributed`
      + (redist.refused.length ? `, ${redist.refused.length} left alone` : "")
      + " - Save schedule to persist, or Undo redistribute",
      8,
    );
  };

  const bulkDelete = () => {
    let withChildren = false;
    Modal.confirm({
      title: `Delete ${selectedKeys.length} selected row${selectedKeys.length === 1 ? "" : "s"}?`,
      content: (
        <div>
          <div style={{ color: "#666", marginBottom: 8 }}>Removed from the draft; Save schedule deletes the saved activities.</div>
          <Checkbox onChange={(e) => { withChildren = e.target.checked; }}>
            Also delete everything under selected summaries
          </Checkbox>
        </div>
      ),
      okText: "Delete", okButtonProps: { danger: true },
      onOk: () => {
        const kill = new Set();
        rows.forEach((r, i) => {
          if (!selectedSet.has(r.key)) return;
          if (withChildren) rows.slice(i, subtreeEnd(rows, i)).forEach((x) => kill.add(x.key));
          else kill.add(r.key);
        });
        // One pass: drop the rows and every link that pointed at them.
        setRows((rs) => rs.filter((r) => !kill.has(r.key)).map((r) => {
          const preds = r.predecessors || [];
          const kept = preds.filter((p) => !kill.has(typeof p === "string" ? p : p && p.key));
          return kept.length === preds.length ? r : { ...r, predecessors: kept };
        }));
        setSelectedKeys([]);
        message.success(`${kill.size} row${kill.size === 1 ? "" : "s"} removed - Save schedule to persist`);
      },
    });
  };

  // On a plain row: a sibling right below it. On an open section: its
  // first child (a sibling there would adopt the section's children). On
  // a folded section: a sibling after everything folded under it.
  const insertBelow = (idx) =>
    setRows((rs) => {
      const next = [...rs];
      const base = rs[idx]?.indent || 0;
      if (hasChildrenAt(rs, idx)) {
        if (collapsed.has(rs[idx].key)) next.splice(subtreeEnd(rs, idx), 0, newRow({ indent: base }));
        else next.splice(idx + 1, 0, newRow({ indent: base + 1 }));
      } else {
        next.splice(idx + 1, 0, newRow({ indent: base }));
      }
      return next;
    });

  // Preview items for the shared gantt (client-side pseudo-ids).
  const previewItems = useMemo(() => {
    return computed.rows
      .filter((r) => r.name.trim() && r.startDate)
      .map((r, i) => ({
        _id: r.key,
        // The draft key is what the editor works in, but the row still
        // knows the activity it came from - and anything keyed on the
        // real id (its coordination areas) needs that, not the key.
        srcId: r._id ? String(r._id) : "",
        name: `${r.wbs} ${r.name}`,
        type: r.type || (r.milestone ? "milestone" : "task"),
        startDate: r.startDate,
        endDate: r.endDate,
        status: r.status || "planned",
        progress: r.progress || 0,
        responsibleCompanyId: r.responsibleCompanyId || "",
        responsibleUserName: (r.responsibleUsers || []).map((u) => u.name).filter(Boolean).join(", "),
        contractorLabel: r.contractorLabel || "",
        disabled: !!r.disabled,
        signOffDate: r.signOffDate || null, signedOffAt: r.signedOffAt || null, signedOffBy: r.signedOffBy || null,
        signOffDateOriginal: r.signOffDate && r.signOffDateOriginal ? r.signOffDateOriginal : null,
        ownerSignOff: r.ownerSignOff || null,
        discipline: r.discipline || "",
        phase: r.phase || "", step: r.step || "", workPackage: r.workPackage || "", gate: r.gate || null,
        trackingProgress: r.trackingProgress === undefined ? null : r.trackingProgress,
        trackingProgressCount: r.trackingProgressCount || 0,
        parentId: r.parentRow != null ? computed.rows[r.parentRow]?.key : null,
        dependencies: r.predKeys || [],
        issueCounts: {},
      }));
  }, [computed]);
  const chartItems = useDebounced(previewItems, 150);

  // Sign off / withdraw is immediate - its own gated route - unlike the
  // rest of the row, which waits for Save. What blocks it (open issues,
  // dated children not signed off) comes back as a 409 and is shown,
  // with the choice to sign off anyway.
  const toggleSignOff = async (r) => {
    if (!r._id) return;
    const apply = (body) => setRow(r.key, { signedOffAt: body.signedOffAt || null, signedOffBy: body.signedOffBy || null });
    try {
      if (r.signedOffAt) {
        await withdrawSignOffGanttItem(projectId, r._id);
        setRow(r.key, { signedOffAt: null, signedOffBy: null });
        message.success(`Sign-off withdrawn on "${r.name}"`);
        return;
      }
      // An unsaved date on the row travels with the sign-off.
      apply(await signOffGanttItem(projectId, r._id, { signOffDate: r.signOffDate || undefined }));
      message.success(`"${r.name}" signed off`);
    } catch (e) {
      const open = Number(e && e.openIssues) || 0;
      const kids = (e && e.data && e.data.unsignedChildren) || [];
      if (e && e.status === 409 && (open || kids.length)) {
        Modal.confirm({
          title: `"${r.name}" is not ready to sign off`,
          content: (
            <div>
              {open ? <div>{open} open issue{open === 1 ? "" : "s"} on it.</div> : null}
              {kids.length ? <div>Not signed off yet: {kids.map((k) => k.name).join(", ")}</div> : null}
              <div style={{ marginTop: 8, color: "#888" }}>Sign off anyway?</div>
            </div>
          ),
          okText: "Sign off anyway", okButtonProps: { danger: true },
          onOk: async () => {
            try {
              apply(await signOffGanttItem(projectId, r._id, { force: true, signOffDate: r.signOffDate || undefined }));
              message.warning(`"${r.name}" signed off with open work behind it`);
            } catch (e2) { message.error(e2.message || "Sign-off failed"); }
          },
        });
        return;
      }
      message.error((e && e.message) || "Sign-off failed");
    }
  };

  // Early start on a field row (before its package's BIM sign-off): asked
  // for / approved immediately through its own PATCH, then the gates are
  // re-read from the feed and merged onto the draft rows - the rest of the
  // draft is left as it is.
  const refreshGates = async () => {
    try {
      const res = await getGanttFeed(projectId);
      const items = res?.items || [];
      setExistingItems(items);
      const byId = new Map(items.map((it) => [String(it._id), it]));
      setRows((rs) => rs.map((r) => {
        const it = r._id ? byId.get(String(r._id)) : null;
        return it ? { ...r, gate: it.gate || null, earlyStart: it.earlyStart || null, trackingProgress: it.trackingProgress === undefined ? null : it.trackingProgress, trackingProgressCount: it.trackingProgressCount || 0 } : r;
      }));
    } catch (e) { /* the next load shows it */ }
  };
  const approveEarly = async (r) => {
    try {
      await updateGanttItem(projectId, r._id, { earlyStart: { approved: true } });
      message.success(`Early start approved on "${r.name}"`);
      await refreshGates();
    } catch (e) { message.error((e && e.message) || "Approval failed"); }
  };
  const requestEarly = (r) => {
    let reason = "";
    Modal.confirm({
      title: `Request an early start on "${r.name}"`,
      content: (
        <div>
          <div style={{ marginBottom: 6, color: "#666" }}>It starts before its BIM sign-off. Why does it need to?</div>
          <Input.TextArea rows={3} autoFocus onChange={(e) => { reason = e.target.value; }} />
        </div>
      ),
      okText: "Request",
      onOk: async () => {
        if (!reason.trim()) { message.warning("Give a reason"); throw new Error("reason"); }
        try {
          await updateGanttItem(projectId, r._id, { earlyStart: { requested: true, reason: reason.trim() } });
          message.success("Early start requested");
          await refreshGates();
        } catch (e) { message.error((e && e.message) || "Request failed"); }
      },
    });
  };

  const save = async () => {
    const valid = computed.rows.filter((r) => r.name.trim() && r.endDate);
    if (!valid.length) return message.warning("Add at least one named row");
    if (computed.errors.length) {
      return message.error(`Fix schedule errors first: ${computed.errors[0]}`);
    }
    setSaving(true);
    const snaps = snapshotRef.current;
    const progressKey = "schedule-save-progress";
    const showProgress = (done, total, what) => {
      setSaveProgress({ done, total });
      message.open({ key: progressKey, type: "loading", content: `${what} ${done} of ${total}…`, duration: 0 });
    };
    try {
      // Sort keys that keep the saved order where they can: a moved block
      // rewrites the moved rows, not every row after it.
      const sortIdx = assignSortIndexes(valid.map((r) => {
        const s = r._id ? snaps.get(String(r._id)) : null;
        return s ? s.sortIndex : null;
      }));
      const sortOf = new Map(valid.map((r, i) => [r.key, sortIdx[i]]));
      // Pass 1 — NEW rows are created (bulk) in visual order; existing rows
      // (loaded from the plan, carrying _id) are updated in place.
      const fresh = valid.filter((r) => !r._id);
      let createdByKey = new Map();
      const createdItems = [];
      if (fresh.length) {
        const items = fresh.map((r) => ({
          name: r.name.trim(),
          type: r.type || "task",
          startDate: r.startDate,
          endDate: r.endDate,
          wbs: r.wbs,
          sortIndex: sortOf.get(r.key),
          discipline: r.discipline || "", areaId: r.areaId || null, calendarId: r.calendarId || null,
          areaKey: r.areaKey || "", level: r.level || "", grid: r.grid || "",
          kind: r.kind || "", category: r.category || "", stage: r.stage || "",
          responsibleCompanyId: r.responsibleCompanyId || "",
          responsibleUsers: (r.responsibleUsers || []).map((u) => ({ userId: u.value, name: u.name || "" })),
          contractorLabel: r.contractorLabel || "",
          disabled: !!r.disabled,
          signOffDate: r.signOffDate || null,
          phase: r.phase || "", step: r.step || "", workPackage: r.workPackage || "",
          status: r.status || "planned",
          progress: r.status === "done" ? 100 : (r.progress || 0),
          ...(r.type === "meeting" && r.recurrence ? { recurrence: r.recurrence } : {}),
        }));
        message.open({ key: progressKey, type: "loading", content: `Creating ${fresh.length} new activit${fresh.length === 1 ? "y" : "ies"}…`, duration: 0 });
        const res = await fetch(`${url}/projects/${projectId}/gantt-items/bulk`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...auth() },
          body: JSON.stringify({ items }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
        (body.items || []).forEach((it, i) => {
          createdByKey.set(fresh[i].key, it._id);
          createdItems.push(it);
          snaps.set(String(it._id), snapshotFromItem(it));
        });
      }
      const idOfRow = (r) => r._id || createdByKey.get(r.key) || null;
      const rowByKey = new Map(computed.rows.map((r) => [r.key, r]));

      // Pass 2 — nesting + logic ties (+ full field update for existing
      // rows), sent ONLY for rows whose patch differs from what was loaded,
      // six at a time.
      const jobs = [];
      // A parent's % is the average of its tasks (back/utils/ganttProgress):
      // the server refuses a typed one, so it is never sent; and a leaf's is
      // sent only when it changed, so resending the loaded number cannot
      // trip the server's who-may-set-progress check.
      const parentRowObjs = new Set(computed.rows.filter((x) => x.parentRow != null).map((x) => computed.rows[x.parentRow]));
      for (const r of valid) {
        const id = idOfRow(r);
        if (!id) continue;
        const patch = {};
        const parent = r.parentRow != null ? computed.rows[r.parentRow] : null;
        patch.parentId = parent ? idOfRow(parent) : null;
        // Links leave the editor in the API's shape: predecessor id,
        // relationship type and lag (utils/dependency.js).
        patch.dependencies = toApiDependencies(
          r.predKeys,
          new Map((r.predKeys || [])
            .map((l) => { const pr = rowByKey.get(l.key); const pid = pr ? idOfRow(pr) : null; return pid ? [l.key, pid] : null; })
            .filter(Boolean))
        );
        patch.sortIndex = sortOf.get(r.key);
        if (r._id) {
          Object.assign(patch, {
            name: r.name.trim(), type: r.type || "task", startDate: r.startDate, endDate: r.endDate, wbs: r.wbs,
            kind: r.kind || "", category: r.category || "", stage: r.stage || "",
            discipline: r.discipline || "", areaId: r.areaId || null, calendarId: r.calendarId || null,
            areaKey: r.areaKey || "", level: r.level || "", grid: r.grid || "",
            responsibleCompanyId: r.responsibleCompanyId || "",
            responsibleUsers: (r.responsibleUsers || []).map((u) => ({ userId: u.value, name: u.name || "" })),
            contractorLabel: r.contractorLabel || "",
            disabled: !!r.disabled,
            signOffDate: r.signOffDate || null,
            phase: r.phase || "", step: r.step || "", workPackage: r.workPackage || "",
            // Ticking Done here also closes the issues / checklist items
            // on the activity (server-side cascade).
            status: r.status || "planned",
            progress: r.status === "done" ? 100 : (r.progress || 0),
          });
        } else if (!patch.parentId && !patch.dependencies.length) {
          // A new top-level row with no links: the bulk create already
          // wrote everything, sortIndex included.
          continue;
        }
        if (patch.progress !== undefined && (parentRowObjs.has(r) || !changedFields({ progress: patch.progress }, snaps.get(String(id))).length)) {
          delete patch.progress;
        }
        if (!changedFields(patch, snaps.get(String(id))).length) continue;
        jobs.push({ r, id, patch, existing: !!r._id });
      }
      let updated = 0;
      const cascaded = { issues: 0, checklist: 0, activities: 0 };
      const failed = [];
      if (jobs.length) showProgress(0, jobs.length, "Saving");
      const results = await runPool(jobs, 6, async (job) => {
        const pres = await fetch(`${url}/projects/${projectId}/gantt-items/${job.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...auth() },
          body: JSON.stringify(job.patch),
        });
        let pbody = null;
        try { pbody = await pres.json(); } catch (e) { /* body is optional */ }
        if (!pres.ok) throw new Error((pbody && pbody.err) || `HTTP ${pres.status}`);
        return pbody;
      }, (done, total) => showProgress(done, total, "Saving"));
      results.forEach((res, i) => {
        const job = jobs[i];
        if (!res.ok) { failed.push({ name: job.r.name, err: res.error && res.error.message, existing: job.existing }); return; }
        if (job.existing) updated += 1;
        // What the server now holds - the next Save compares against it.
        snaps.set(String(job.id), { ...(snaps.get(String(job.id)) || {}), ...job.patch });
        // Ticking Done closes the issues / checklist items on the
        // activity - total it up so the save message says so.
        const pbody = res.value;
        if (pbody && pbody.cascade) {
          cascaded.issues += pbody.cascade.issues || 0;
          cascaded.checklist += pbody.cascade.checklist || 0;
          cascaded.activities += pbody.cascade.activities || 0;
        }
      });
      // Pass 3 — existing items removed from the editor → deleted.
      let removed = 0;
      const removedIds = new Set();
      if (loadedFromPlan) {
        const keep = new Set(valid.map((r) => r._id).filter(Boolean).map(String));
        const gone = existingItems.filter((it) => !keep.has(String(it._id)));
        if (gone.length) showProgress(0, gone.length, "Deleting");
        const del = await runPool(gone, 6, (it) => deleteGanttItem(projectId, it._id),
          (done, total) => showProgress(done, total, "Deleting"));
        del.forEach((d, i) => {
          if (d.ok) { removed += 1; removedIds.add(String(gone[i]._id)); snaps.delete(String(gone[i]._id)); }
        });
      }
      message.destroy(progressKey);
      const closedBits = [
        cascaded.issues ? `${cascaded.issues} issue${cascaded.issues === 1 ? "" : "s"} resolved` : "",
        cascaded.checklist ? `${cascaded.checklist} checklist item${cascaded.checklist === 1 ? "" : "s"} completed` : "",
        cascaded.activities ? `${cascaded.activities} child activit${cascaded.activities === 1 ? "y" : "ies"} done` : "",
      ].filter(Boolean).join(", ");
      const unchanged = valid.filter((r) => r._id).length - updated - failed.filter((f) => f.existing).length;
      if (failed.length) {
        // Stay on the page: the new rows now exist, so they keep their ids
        // (a second Save must not create them again), and only what failed
        // - plus anything edited since - is sent next time.
        setRows((rs) => rs.map((r) => (createdByKey.has(r.key) ? { ...r, _id: createdByKey.get(r.key) } : r)));
        setExistingItems((items) => [...items.filter((it) => !removedIds.has(String(it._id))), ...createdItems]);
        setLoadedFromPlan(true);
        message.error(`${failed.length} of ${jobs.length} updates failed (first: "${failed[0].name}" - ${failed[0].err || "error"}). ${fresh.length} added, ${updated} updated${removed ? `, ${removed} removed` : ""}. Save again to retry.`, 10);
        return;
      }
      message.success(`Schedule saved - ${fresh.length} added, ${updated} updated${unchanged > 0 ? ` (${unchanged} unchanged, not sent)` : ""}${removed ? `, ${removed} removed` : ""}${closedBits ? ` (${closedBits})` : ""}`);
      // Back to Coordination with its project bar (Select project, Full
      // dashboard, Partners, Tracking), on this project. /coordination/:id
      // is the full dashboard, which has no bar - the owner read that as the
      // bar disappearing after Edit schedule.
      history.push(`/coordination?projectId=${projectId}`);
    } catch (e) {
      message.destroy(progressKey);
      message.error(e.message || "Save failed");
    } finally {
      setSaving(false);
      setSaveProgress(null);
    }
  };

  // Rows shown in the table: all of them, or those matching the search
  // (name, WBS, type, discipline, area / level / grid, partner, person).
  // Row numbers and row actions always use the position in the FULL list
  // (predecessors are entered as row numbers, stored as row keys).
  const idxByKey = useMemo(() => new Map(rows.map((r, i) => [r.key, i])), [rows]);
  const query = search.trim().toLowerCase();
  const filtersOn = !!query || todayOnly || !rangeEmpty(startRange) || !rangeEmpty(endRange);
  const visibleRows = useMemo(() => {
    const dated = !rangeEmpty(startRange) || !rangeEmpty(endRange);
    // Collapsed sections fold the table only when nothing is being
    // searched for: a match under a folded section must still show.
    if (!query && !todayOnly && !dated) return visibleUnderCollapse(rows, collapsed);
    const todayIso = new Date().toISOString().slice(0, 10);
    return rows.filter((r) => {
      const c = computedByKey.get(r.key);
      if (todayOnly) {
        if (!c?.startDate || c.startDate > todayIso || r.status === "done") return false;
      }
      // Computed start / finish, already 'YYYY-MM-DD' on this page - the
      // same values the Start and Finish columns show, so what the
      // filter says and what the row reads always agree.
      if (!inDayRange(c?.startDate || "", startRange.from, startRange.to)) return false;
      if (!inDayRange(c?.endDate || "", endRange.from, endRange.to)) return false;
      if (!query) return true;
      const co = roster.find((g) => String(g.companyId) === String(r.responsibleCompanyId));
      const picked = new Set((r.responsibleUsers || []).map((u) => String(u.value)));
      const persons = (co?.users || []).filter((u) => picked.has(String(u.userId || `email:${String(u.email || "").toLowerCase()}`)));
      const labelOf = (kind, key) => {
        const row = (taxonomy[kind] || []).find((t) => t.key === key);
        return row ? row.label : key;
      };
      const hay = [
        r.name, c?.wbs, r.type, c?.isSummary ? "summary" : "", r.discipline, r.areaKey, r.level, r.grid,
        r.category && labelOf("activity_category", r.category), r.stage && labelOf("project_stage", r.stage),
        r.kind && labelOf("milestone_kind", r.kind), r.phase, r.step, r.workPackage,
        co?.companyName, (co?.trades || []).join(" "), r.contractorLabel, ...persons.map((p) => `${p.name || ""} ${p.email || ""}`), ...(r.responsibleUsers || []).map((u) => u.name || ""), r.status,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(query);
    });
  }, [rows, collapsed, query, todayOnly, startRange.from, startRange.to, endRange.from, endRange.to, computedByKey, roster, taxonomy]);

  const columns = [
    { title: "#", width: 40, render: (_, r) => (idxByKey.get(r.key) ?? 0) + 1 },
    {
      title: <Tooltip title="Tick to disable the activity: shown barred, left out of the emailed schedule, health and reminders; its issues and checklist items are abandoned while disabled"><span style={{ color: "#888" }}>Off</span></Tooltip>,
      key: "off",
      width: 40,
      render: (_, r) => (
        <input type="checkbox" checked={!!r.disabled} style={{ cursor: "pointer", accentColor: "#8c8c8c" }}
               title={r.disabled ? "Disabled - untick to enable" : "Disable this activity"}
               onChange={(e) => setRow(r.key, { disabled: e.target.checked })} />
      ),
    },
    {
      title: <Tooltip title="Tick to mark the activity done: it is completed on the schedule and the work hanging off it is finished too - its issues / actions are closed and its checklist items completed (child activities follow). Untick to put them all back."><span style={{ color: "#888" }}>Done</span></Tooltip>,
      key: "done",
      width: 48,
      render: (_, r) => (
        <input type="checkbox" checked={r.status === "done"} style={{ cursor: "pointer", accentColor: "#2e7d32" }}
               disabled={!!r.disabled}
               title={r.status === "done" ? "Done - untick to reopen" : "Mark done (closes its issues and checklist items)"}
               onChange={(e) => {
                 // The tickbox, the Status column and both bulk setters all
                 // call statusPatch, so none of them can do something the
                 // others would refuse (statusEditOps).
                 //
                 // AND IT TAKES THE WHOLE SUBTREE WITH IT. This column's own
                 // tooltip has promised "(child activities follow)" and
                 // "Untick to put them all back" since it was built, and the
                 // handler patched ONE row - so a parent went green with its
                 // children left open, which is the one thing a summary row
                 // must never be able to say. Owner, 2026-10-01: "why is the
                 // check box on the schedule doesn't also automatically
                 // cehckbox all children recursively".
                 const next = e.target.checked ? "done" : "in_progress";
                 const { changes } = statusCascade(rows, r.key, next, { subtreeKeys });
                 if (!changes.length) return;
                 setRows((rs) => {
                   const byKey = new Map(changes.map((c) => [c.key, c.patch]));
                   return rs.map((row) => (byKey.has(row.key)
                     ? { ...row, ...byKey.get(row.key), _loadedEnd: row._loadedEnd }
                     : row));
                 });
                 // Said, because sixty rows changing from one click is worth
                 // one sentence rather than a silent repaint.
                 if (changes.length > 1) {
                   message.success(`${changes.length} activities ${next === "done" ? "marked done" : "reopened"}`);
                 }
               }} />
      ),
    },
    {
      // THE TICKBOX COULD ONLY EVER WRITE TWO OF FOUR VALUES. `planned` and
      // `on_hold` were unreachable from this page entirely - not missing
      // from bulk, missing from the product. Owner, 2026-09-29: "then allow
      // per row edit and bulk edit".
      title: <Tooltip title="Where the activity stands. Marking it Done completes the work hanging off it - its issues are closed and its checklist items completed - and moving it off Done puts them back."><span>Status</span></Tooltip>,
      key: "status", width: 118,
      render: (_, r) => (
        <Select size="small" style={{ width: 108 }} value={r.status || "planned"}
                disabled={!!r.disabled}
                title={r.disabled ? "This activity is disabled" : undefined}
                options={STATUS_OPTIONS}
                onChange={(v) => { const patch = statusPatch(r, v); if (patch) setRow(r.key, patch); }} />
      ),
    },
    {
      title: "WBS", width: 60,
      render: (_, r) => {
        const c = computedByKey.get(r.key);
        return <span style={{ color: "#888" }}>{c?.wbs}</span>;
      },
    },
    {
      title: "Name", width: 280,
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 2, paddingLeft: (r.indent || 0) * 14 }}>
          {/* Fold / unfold a section. Only the table hides the rows under
              it - they stay in the draft and are saved. Hidden while a
              search is on, which shows matches whatever is folded. */}
          {hasChildrenAt(rows, idxByKey.get(r.key)) && !filtersOn ? (
            <span role="button" onClick={() => toggleCollapsed(r.key)}
                  title={collapsed.has(r.key) ? "Expand" : "Collapse"}
                  style={{ cursor: "pointer", width: 16, flex: "0 0 16px", color: "#555", fontSize: 11 }}>
              {collapsed.has(r.key) ? <CaretRightOutlined /> : <CaretDownOutlined />}
            </span>
          ) : <span style={{ width: 16, flex: "0 0 16px" }} />}
          {/* The field is 280px wide and indented rows lose more of it, so a
              long activity name reads as "Pile caps, grade beams & foun…".
              Hovering shows the whole one - the same tooltip the coordination
              plan's Name column has. Short names get no tooltip (an empty
              title disables it) so typing in the field is not shadowed. */}
          <Tooltip title={(r.name || "").length > 28 ? <span style={{ fontSize: 12 }}>{r.name}</span> : ""}
                   mouseEnterDelay={0.35} placement="topLeft">
            <Input size="small" value={r.name} placeholder="Task name"
                   style={{ ...(computedByKey.get(r.key)?.isSummary ? { fontWeight: 600 } : {}), ...(r.disabled ? { textDecoration: "line-through", color: "#999", background: "#fafafa" } : {}) }}
                   onChange={(e) => setRow(r.key, { name: e.target.value })} />
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Type", width: 100,
      render: (_, r) => {
        const c = computedByKey.get(r.key);
        // A row with children IS a summary - indentation says so, and the
        // engine spans it over them - so the picker steps aside there.
        return c?.isSummary ? <span style={{ color: "#888" }}>Summary</span> : (
          <Select size="small" value={r.type || "task"} style={{ width: "100%" }}
                  optionLabelProp="label"
                  options={ACTIVITY_TYPES.filter((t) => t.value !== "summary" || r.type === "summary").map((t) => ({
                    value: t.value, label: t.label,
                    title: t.hint,
                  }))}
                  onChange={(v) => setRow(r.key, {
                    type: v,
                    durationDays: ZERO_DURATION_TYPES.includes(v) ? 0 : (r.durationDays || 1),
                    // A behaviour change can invalidate the meaning: a task
                    // is not a Notice To Proceed.
                    kind: isMilestone(v) ? r.kind : "",
                  })} />
        );
      },
    },
    {
      title: "Dur (d)", width: 70,
      render: (_, r) => {
        const c = computedByKey.get(r.key);
        if (c?.isSummary) return <Tooltip title={SUMMARY_DATES_TIP}><span style={{ color: "#bbb" }}>—</span></Tooltip>;
        return isZeroDur(r) ? (
          <Tooltip title="Milestones have no duration - set the date in Start or Finish"><span style={{ color: "#bbb" }}>0</span></Tooltip>
        ) : (
          <InputNumber size="small" min={1} max={999} value={r.durationDays}
                       onChange={(v) => setRow(r.key, { durationDays: v || 1 })} />
        );
      },
    },
    {
      title: "Start", width: 108,
      render: (_, r) => {
        const c = computedByKey.get(r.key);
        // A SUMMARY'S START IS EDITABLE NOW, with its earliest child as the
        // ceiling (owner, 2026-10-01). Blank clears the pin and the row goes
        // back to rolling up.
        return c?.isSummary ? (
          <Tooltip title={SUMMARY_FLOOR_TIP}>
            <input type="date" value={r.manualStart || c?.startDate || ""}
                   style={{ border: r.manualStart ? "1px solid #1a1a2e" : "1px solid #eee", borderRadius: 4, padding: "1px 4px", fontSize: 12, width: 118, background: "#fff" }}
                   onChange={(e) => setSummaryDate(r.key, "start", e.target.value)} />
          </Tooltip>
        ) : (
          <Tooltip title="Leave empty to auto-schedule; set to pin a start date">
            <input type="date" value={r.manualStart || c?.startDate || ""}
                   style={{ border: r.manualStart ? "1px solid #1a1a2e" : "1px solid #eee", borderRadius: 4, padding: "1px 4px", fontSize: 12, width: 118 }}
                   onChange={(e) => setRow(r.key, { manualStart: e.target.value })} />
          </Tooltip>
        );
      },
    },
    {
      // Finish used to be a read-only readout of Start + Duration, so the
      // only way to say "this ends on the 14th" was to count the working
      // days yourself and type them into Dur — and on a NEW activity that
      // reads as "the end date is greyed out". It is a date box now, and
      // it does exactly what dragging the bar's right edge already did:
      // the date is converted to a working-day duration and the engine
      // reschedules from there (see onGanttLocalChange). Duration stays
      // the stored quantity, so predecessors and calendars keep working.
      title: "Finish", width: 108,
      render: (_, r) => {
        const c = computedByKey.get(r.key);
        // A SUMMARY'S FINISH IS EDITABLE NOW, with its latest child as the
        // floor - his words: "the lowest date a parent can take is the last
        // date/finish of his latest child".
        if (c?.isSummary) {
          return (
            <Tooltip title={SUMMARY_FLOOR_TIP}>
              <input type="date" value={r.manualEnd || c?.endDate || ""}
                     style={{ border: r.manualEnd ? "1px solid #1a1a2e" : "1px solid #eee", borderRadius: 4, padding: "1px 4px", fontSize: 12, width: 118, background: "#fff" }}
                     onChange={(e) => setSummaryDate(r.key, "end", e.target.value)} />
            </Tooltip>
          );
        }
        // A milestone is one date, so its finish IS its start: editing
        // either pins it (a finish milestone is naturally set here).
        if (isZeroDur(r)) {
          return (
            <Tooltip title="Milestone date - start and finish are the same day; setting it pins the milestone">
              <input type="date" value={r.manualStart || c?.endDate || ""}
                     style={{ border: r.manualStart ? "1px solid #1a1a2e" : "1px solid #eee", borderRadius: 4, padding: "1px 4px", fontSize: 12, width: 118 }}
                     onChange={(e) => setRow(r.key, { manualStart: e.target.value })} />
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Set the finish date; the duration is worked out from it in working days. Weekends and the row's calendar still apply, so the bar may land on the next working day.">
            <input type="date" value={c?.endDate || ""} min={c?.startDate || undefined}
                   style={{ border: "1px solid #eee", borderRadius: 4, padding: "1px 4px", fontSize: 12, width: 118 }}
                   onChange={(e) => {
                     const v = e.target.value;
                     if (!v || !c?.startDate) return;
                     setRow(r.key, { durationDays: workingDaysBetween(c.startDate, v, r.calendarId || null), _loadedEnd: undefined });
                   }} />
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Sign-off date: when this activity is to be formally signed off. Shown as a star on the schedule; the weekly and monthly reports count the days left. Leave empty for activities that need none (safety, general conditions, meetings). Signing off needs every issue on the row resolved and every dated child activity signed off first - BIM after its disciplines."><span>Sign-off <span style={{ color: "#d9a400" }}>★</span></span></Tooltip>,
      key: "signoff", width: 150,
      render: (_, r) => {
        const signed = !!r.signedOffAt;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <input type="date" value={r.signOffDate || ""} disabled={signed || !!r.disabled}
                   title={signed ? "Withdraw the sign-off to change the date" : "Planned sign-off date (empty = none needed)"}
                   style={{ border: r.signOffDate ? "1px solid #d9a400" : "1px solid #eee", borderRadius: 4, padding: "1px 4px", fontSize: 12, width: 118 }}
                   onChange={(e) => { const patch = signOffPatch(r, e.target.value); if (patch) setRow(r.key, patch); }} />
            {r._id && r.signOffDate ? (
              <Tooltip title={signed
                ? `Signed off ${String(r.signedOffAt).slice(0, 10)}${r.signedOffBy && r.signedOffBy.name ? ` by ${r.signedOffBy.name}` : ""} - click to withdraw`
                : "Sign off now. Needs every issue on it resolved and every dated child signed off; anything blocking it is listed."}>
                <span role="button" onClick={() => toggleSignOff(r)}
                      style={{ cursor: "pointer", fontSize: 17, lineHeight: 1, color: signed ? "#2e7d32" : "#d9a400" }}>{signed ? "★" : "☆"}</span>
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Preds", width: 96,
      render: (_, r) => {
        // Typed and shown as row numbers, stored as the keys of those rows:
        // the number is just where the row sits right now, so moving or
        // inserting rows renumbers this box without changing the logic.
        const shown = predsToText(r.predecessors, idxByKey);
        const commit = (txt) => setRow(r.key, { predecessors: textToPreds(txt, rows, r.key) });
        return (
          <Tooltip title={(
            <span>
              Predecessors by row number: <b>2,3</b>.<br />
              A relationship and a lag can follow the number —{" "}
              <b>2SS</b> (starts when 2 starts), <b>2FF</b> (finishes when 2
              finishes), <b>2FS+3d</b> (three working days after 2 finishes),{" "}
              <b>2SS-1d</b> (a day before 2 starts).<br />
              A link follows the row it points at, so re-ordering the
              schedule keeps it.
            </span>
          )}>
            <Input size="small" placeholder="—"
                   value={predDraft[r.key] !== undefined ? predDraft[r.key] : shown}
                   onChange={(e) => { setPredDraft((d) => ({ ...d, [r.key]: e.target.value })); commit(e.target.value); }}
                   onBlur={() => setPredDraft((d) => { const n = { ...d }; delete n[r.key]; return n; })} />
          </Tooltip>
        );
      },
    },
    {
      title: "Float", width: 64,
      render: (_, r) => {
        const c = computedByKey.get(r.key);
        if (!c || c.isSummary || c.totalFloatDays == null) return <span style={{ color: "#bbb" }}>—</span>;
        // Zero float means the project finish moves the day this row does
        // - that is the whole point of the backward pass, so it is the one
        // number worth colouring.
        const crit = c.critical;
        return (
          <Tooltip title={crit
            ? `Critical: no slack. Late finish ${c.lateFinish || "—"}`
            : `Can slip ${c.totalFloatDays} working day${c.totalFloatDays === 1 ? "" : "s"} before the finish moves (late start ${c.lateStart || "—"})`}>
            <span style={{ color: crit ? "#d4380d" : "#389e0d", fontWeight: crit ? 700 : 400 }}>
              {crit ? "0 ⚠" : c.totalFloatDays}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Calendar", width: 96,
      render: (_, r) => {
        const c = computedByKey.get(r.key);
        // A summary takes its dates from its children, so its own
        // working week decides nothing.
        if (c?.isSummary) return <span style={{ color: "#bbb" }}>—</span>;
        const def = (calendars || []).find((x) => x.isDefault);
        return (
          <Tooltip title={r.calendarId
            ? describeCalendar((calendars || []).find((x) => String(x._id) === String(r.calendarId)))
            : `Project default${def ? `: ${def.name} · ${describeCalendar(def)}` : ""}`}>
            <Select filterOption={matchOption} size="small" style={{ width: "100%" }} allowClear
                    placeholder={def ? `Default · ${describeCalendar(def)}` : "Default"}
                    value={r.calendarId || undefined}
                    options={calendarOptions} optionFilterProp="label" showSearch
                    onChange={(v) => setRow(r.key, { calendarId: v || null })} />
          </Tooltip>
        );
      },
    },
    {
      title: "Responsible", width: 170,
      render: (_, r) => {
        const c = computedByKey.get(r.key);
        if (c?.isSummary) return <span style={{ color: "#bbb" }}>—</span>;
        const group = roster.find((g) => String(g.companyId) === String(r.responsibleCompanyId));
        // Partner contacts without an account are selectable too: saving
        // the schedule gives them a magic-login account (like assigning an
        // issue to them) and the row keeps the real user id.
        // The shared picker's people (components/PeoplePicker): tagged by
        // kind, a partner executive greyed out - a reader is never
        // responsible.
        const people = schedulePeopleOf(group);
        // Contractor: a partner company on the project, or - for rows that
        // came from documents / are typed by hand - a contractor label
        // ("BES", "DP") kept on the row until it is mapped to a company
        // (Map contractors). Typing a name that is not a partner offers
        // "use as label".
        const typed = (labelDraft[r.key] || "").trim();
        const labelValue = !r.responsibleCompanyId && r.contractorLabel ? `label:${r.contractorLabel}` : undefined;
        const contractorOptions = [
          ...companyOptions,
          ...(labelValue ? [{ value: labelValue, label: `${r.contractorLabel} (label - not mapped yet)` }] : []),
          ...(typed && !companyOptions.some((o) => [o.label, o.name].some((n) => String(n || "").toLowerCase() === typed.toLowerCase())) && typed.toUpperCase() !== (r.contractorLabel || "")
            ? [{ value: `label:${typed.toUpperCase()}`, label: `Use "${typed}" as contractor label` }] : []),
        ];
        return (
          <Space size={4} style={{ width: "100%" }}>
            <Select size="small" allowClear showSearch placeholder="Partner or label" value={r.responsibleCompanyId || labelValue}
                    options={contractorOptions} optionFilterProp="label"
                    filterOption={(input, option) => String(option?.value || "").startsWith("label:") || String(option?.label || "").toLowerCase().includes(String(input).toLowerCase())}
                    onSearch={(txt) => setLabelDraft((d) => ({ ...d, [r.key]: txt }))}
                    style={{ width: 150 }}
                    title={r.contractorLabel ? `Label from documents: ${r.contractorLabel}` : undefined}
                    onChange={(v) => {
                      setLabelDraft((d) => ({ ...d, [r.key]: "" }));
                      if (!v) return setRow(r.key, { responsibleCompanyId: undefined, responsibleUserId: undefined, responsibleUsers: [], contractorLabel: "" });
                      if (String(v).startsWith("label:")) return setRow(r.key, { responsibleCompanyId: undefined, responsibleUserId: undefined, responsibleUsers: [], contractorLabel: String(v).slice(6) });
                      setRow(r.key, { responsibleCompanyId: v, responsibleUserId: undefined, responsibleUsers: [] });
                    }} />
            <Select size="small" allowClear showSearch mode="multiple" maxTagCount="responsive" disabled={!r.responsibleCompanyId}
                    value={(r.responsibleUsers || []).map((u) => u.value)}
                    placeholder={r.responsibleCompanyId ? (people.length ? "People (all get the daily follow-up)" : "No people") : "Partner first"}
                    options={people} style={{ width: 190 }} popupMatchSelectWidth={false}
                    optionRender={renderPeopleOption} maxTagPlaceholder={peopleTagPlaceholder(people)}
                    filterOption={(input, option) => String(option?.search || option?.label || "").toLowerCase().includes(input.toLowerCase())}
                    onChange={(vals, opts) => setRow(r.key, {
                      responsibleUsers: (vals || []).map((v, i) => ({ value: v, name: (Array.isArray(opts) && opts[i] && opts[i].name) || (people.find((p) => p.value === v) || {}).name || "" })),
                      responsibleUserId: (vals || [])[0] || undefined,
                    })} />
          </Space>
        );
      },
    },
    {
      // One column, two meanings - because for a diamond, "what kind of
      // work is this" IS which milestone it is. A task carries an activity
      // category (install, inspection, commissioning); a milestone carries
      // its kind (NTP, energization, substantial completion).
      title: <Tooltip title="What kind of work this is. For a milestone, which milestone it is - the company's lists, editable in company settings"><span>Category</span></Tooltip>,
      key: "category",
      width: 118,
      render: (_, r) => {
        const c = computedByKey.get(r.key);
        if (c?.isSummary) return <span style={{ color: "#bbb" }}>—</span>;
        const ms = isMilestone(r.type);
        const opts = asOptions(ms ? taxonomy.milestone_kind : taxonomy.activity_category);
        return (
          <Select filterOption={matchOption} size="small" allowClear showSearch style={{ width: "100%" }}
                  placeholder={ms ? "Which milestone" : "Kind of work"}
                  value={(ms ? r.kind : r.category) || undefined}
                  options={opts} optionFilterProp="label"
                  notFoundContent={opts.length ? "No match" : "No list yet - seed it in company settings"}
                  onChange={(v) => setRow(r.key, ms ? { kind: v || "" } : { category: v || "" })} />
        );
      },
    },
    {
      title: <Tooltip title="Which stage of the job this belongs to - Preconstruction through Turnover. What the stage rollups and the executive view are built on"><span>Stage</span></Tooltip>,
      key: "stage",
      width: 110,
      render: (_, r) => {
        const opts = asOptions(taxonomy.project_stage);
        return (
          <Select filterOption={matchOption} size="small" allowClear showSearch style={{ width: "100%" }} placeholder="Stage"
                  value={r.stage || undefined} options={opts} optionFilterProp="label"
                  onChange={(v) => setRow(r.key, { stage: v || "" })} />
        );
      },
    },
    {
      title: <Tooltip title="BIM-to-Field: which chain the activity belongs to - BIM (model, coordinate, sign-off), Field (install) or management"><span>Phase</span></Tooltip>,
      key: "phase", width: 78,
      render: (_, r) => (
        <Select size="small" allowClear style={{ width: "100%" }} placeholder="—"
                value={r.phase || undefined}
                options={[{ value: "bim", label: "BIM" }, { value: "field", label: "Field" }, { value: "management", label: "Mgmt" }]}
                onChange={(v) => setRow(r.key, { phase: v || "" })} />
      ),
    },
    {
      title: <Tooltip title="The step in the chain. BIM 'signoff' releases the Field chain of the same package; 'field_signoff' is install complete. Type to use another step"><span>Step</span></Tooltip>,
      key: "step", width: 110,
      render: (_, r) => {
        // Human labels, raw values - the routes match on the string.
        const opts = SCHEDULE_STEPS.map((v) => ({ value: v, label: stepLabel(v) }));
        return (
          <Select filterOption={matchOption} size="small" style={{ width: "100%" }} allowClear showSearch
                  mode="tags" maxTagCount={1} placeholder="—"
                  value={r.step ? [r.step] : []}
                  options={!r.step || SCHEDULE_STEPS.includes(r.step) ? opts : [{ value: r.step, label: stepLabel(r.step) }, ...opts]}
                  onChange={(v) => setRow(r.key, { step: (v && v.length ? String(v[v.length - 1]) : "") })} />
        );
      },
    },
    {
      title: <Tooltip title="Work package code (e.g. ELE-CT-L1-D), shared by the BIM and Field chains of one scope"><span>Package</span></Tooltip>,
      key: "workPackage", width: 110,
      render: (_, r) => (
        <Input size="small" value={r.workPackage} placeholder="—"
               onChange={(e) => setRow(r.key, { workPackage: e.target.value.toUpperCase() })} />
      ),
    },
    {
      title: <Tooltip title="Field activities: whether the package's BIM sign-off has released them. Starting before it needs an approved early start"><span>Gate</span></Tooltip>,
      key: "gate", width: 150,
      render: (_, r) => {
        const g = gateTag(r.gate);
        if (!g) return <span style={{ color: "#bbb" }}>—</span>;
        const st = r.gate.state;
        const es = r.earlyStart || {};
        return (
          <Space size={2} wrap>
            <Tooltip title={[
              r.gate.signOffName ? `BIM sign-off: ${r.gate.signOffName}` : "",
              es.reason ? `Reason: ${es.reason}` : "",
            ].filter(Boolean).join(" · ") || undefined}>
              <Tag color={g.color} style={{ marginInlineEnd: 0, fontSize: 11 }}>{g.text}</Tag>
            </Tooltip>
            {r._id && canEditPlan && (st === "out_of_sequence" || st === "early_requested") ? (
              <Button size="small" type="link" style={{ padding: "0 2px", fontSize: 11 }} onClick={() => approveEarly(r)}>Approve early</Button>
            ) : null}
            {r._id && st === "out_of_sequence" && !es.requested ? (
              <Button size="small" type="link" style={{ padding: "0 2px", fontSize: 11 }} onClick={() => requestEarly(r)}>Request</Button>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: <Tooltip title="What the field reports, counted from tracked model elements - not typed here. Click it for the breakdown by category"><span>Field</span></Tooltip>,
      key: "fieldProgress", width: 120,
      render: (_, r) => {
        if (r.phase !== "field" || !r._id) return <span style={{ color: "#bbb" }}>—</span>;
        const p = r.trackingProgress;
        const has = Number.isFinite(Number(p)) && r.trackingProgressCount > 0;
        return (
          <Button size="small" type="link" style={{ padding: 0, fontSize: 12 }}
                  onClick={() => setFieldProgressRow(r)}>
            {has ? (
              <span>
                <b style={{ color: p >= 100 ? "#317A1B" : "#1F4E63" }}>{p}%</b>
                <span style={{ color: "#888" }}> · {r.trackingProgressCount} el.</span>
              </span>
            ) : <span style={{ color: "#999" }}>not tracked</span>}
          </Button>
        );
      },
    },
    {
      title: "Discipline", width: 104,
      render: (_, r) => {
        // The company's trade list, not a text box: "Elec", "ELEC" and
        // "Electrical" used to become three disciplines, and every
        // filter and roll-up downstream believed all three.
        // `mode="tags"` keeps a value the list does not have (imports
        // write their own words) rather than silently dropping it.
        const options = disciplineOptionsWithPartners(disciplines, tradeIx);
        const known = options.some((o) => o.value === r.discipline);
        return (
          <Select filterOption={matchOption} size="small" style={{ width: "100%" }} allowClear showSearch
                  mode="tags" maxTagCount={1} placeholder="—"
                  value={r.discipline ? [r.discipline] : []}
                  options={known || !r.discipline ? options : [{ value: r.discipline, name: r.discipline, text: r.discipline, label: `${r.discipline} (not in the list)` }, ...options]}
                  optionFilterProp="text" optionLabelProp="name" popupMatchSelectWidth={false}
                  onChange={(v) => setRow(r.key, { discipline: (v && v.length ? v[v.length - 1] : "") })} />
        );
      },
    },
    {
      title: "Area / Level / Grid", width: 170,
      render: (_, r) => (
        <Space size={4} direction="vertical" style={{ width: "100%" }}>
          {/* The project's approved areas as the tree they now form.
              areaKey stays as the free text an import wrote, and is
              shown when nothing has been picked - so nothing is lost on
              a project that has not drawn its areas yet. */}
          {/* A TreeSelect renders the raw VALUE when no node matches it,
              so a row pointing at an area that is not in the loaded tree
              — still loading, no longer approved, or deleted — used to
              show a 24-character id. Give it a node to render either
              way, labelled from the row's own text. */}
          {laOn ? (
            <Select filterOption={matchOption} size="small" style={{ width: "100%" }} allowClear showSearch optionFilterProp="label"
                    placeholder={r.areaKey ? `${r.areaKey} (not in Levels & Areas)` : "Area"}
                    value={laValueFor(r)}
                    options={laValueFor(r) && String(laValueFor(r)).startsWith("missing:")
                      ? [{ value: laValueFor(r), label: r.areaKey ? `${r.areaKey} (not in Levels & Areas)` : "Area no longer in Levels & Areas" }, ...laAreaOptions]
                      : laAreaOptions}
                    onChange={(v) => setRow(r.key, laPatchFor(v))} />
          ) : (
          <TreeSelect size="small" style={{ width: "100%" }} allowClear showSearch treeDefaultExpandAll
                      treeNodeFilterProp="title"
                      placeholder={r.areaKey ? `${r.areaKey} (free text)` : "Area"}
                      value={r.areaId || undefined}
                      treeData={
                        !r.areaId || areaNameById.has(String(r.areaId))
                          ? areaTree
                          : [{
                              value: String(r.areaId),
                              title: r.areaKey ? `${r.areaKey} (not in the approved list)` : "Area no longer approved",
                            }, ...areaTree]
                      }
                      onChange={(v) => setRow(r.key, { areaId: v || null, areaKey: v ? (areaNameById.get(String(v)) || r.areaKey) : r.areaKey })} />
          )}
          <Space size={4}>
            {laOn ? (
              <Select filterOption={matchOption} size="small" style={{ width: 110 }} allowClear showSearch placeholder="Level" value={r.level || undefined}
                      options={r.level && !laLevelOptions.some((o) => o.value === r.level)
                        ? [{ value: r.level, label: `${r.level} (not in Levels & Areas)` }, ...laLevelOptions]
                        : laLevelOptions}
                      onChange={(v) => setRow(r.key, { level: v || "" })} />
            ) : (
              <Input size="small" style={{ width: 84 }} value={r.level} placeholder="Level" onChange={(e) => setRow(r.key, { level: e.target.value })} />
            )}
            <Input size="small" style={{ width: 84 }} value={r.grid} placeholder="Grid" onChange={(e) => setRow(r.key, { grid: e.target.value })} />
          </Space>
        </Space>
      ),
    },
    {
      title: "", key: "actions", width: 196,
      render: (_, r) => {
        const i = idxByKey.get(r.key) ?? 0;
        const kids = hasChildrenAt(rows, i);
        return (
        <Space size={2}>
          <Tooltip title="Outdent"><Button size="small" type="text" icon={<ArrowLeftOutlined />}
            disabled={!r.indent} onClick={() => setRow(r.key, { indent: (r.indent || 0) - 1 })} /></Tooltip>
          <Tooltip title="Indent"><Button size="small" type="text" icon={<ArrowRightOutlined />}
            disabled={i === 0 || (r.indent || 0) >= MAX_INDENT}
            onClick={() => setRow(r.key, { indent: (r.indent || 0) + 1 })} /></Tooltip>
          {/* THE BUTTON AND THE MOVE USED TO DISAGREE. These were disabled
              only on the first and last row of the WHOLE LIST, while the move
              itself refuses whenever there is no SIBLING at the same indent
              that way - so on a long plan a parent at the top of its section
              had an enabled button that did nothing and said nothing. The
              owner asked us to "allow" moving a parent with its children: it
              already carried them, he just never saw anything happen.
              canMoveStep derives `can` from moveStep itself, so these two can
              never drift apart again, and the tooltip says WHY when it is
              off - "top of the plan" and "top of your section" are different
              facts and a person acts differently on each. */}
          {(() => {
            const up = canMoveStep(rows, r.key, -1);
            const down = canMoveStep(rows, r.key, 1);
            return (
              <>
                <Tooltip title={up.can ? (kids ? "Move up (with everything under it)" : "Move up") : up.why}>
                  <span>
                    <Button size="small" type="text" icon={<UpOutlined />} disabled={!up.can}
                            onClick={() => move(r.key, -1)} />
                  </span>
                </Tooltip>
                <Tooltip title={down.can ? (kids ? "Move down (with everything under it)" : "Move down") : down.why}>
                  <span>
                    <Button size="small" type="text" icon={<DownOutlined />} disabled={!down.can}
                            onClick={() => move(r.key, 1)} />
                  </span>
                </Tooltip>
              </>
            );
          })()}
          <Tooltip title="Insert a row below"><Button size="small" type="text" icon={<PlusOutlined />} onClick={() => insertBelow(i)} /></Tooltip>
          <Dropdown trigger={["click"]} menu={{
            items: [
              { key: "section", label: "Move to section…" },
              { key: "relative", label: "Move above / below row…" },
              // MOVE THE DATES, not the position in the list - which is why
              // it says "by working days" rather than sitting beside the two
              // moves above it unqualified (owner, 2026-10-05).
              ...(r._id ? [{ key: "offset", label: "Move dates by working days…" }] : []),
              ...(kids ? [
                { type: "divider" },
                { key: "split", label: "Split period across children…" },
                { key: "expand", label: "Expand all below", disabled: filtersOn },
                { key: "collapse", label: collapsed.has(r.key) ? "Expand" : "Collapse", disabled: filtersOn },
              ] : []),
            ],
            onClick: ({ key }) => {
              if (key === "section" || key === "relative") return openMove([r.key], key);
              if (key === "offset") return setOffsetFor(r);
              if (key === "split") return openSplit(r.key);
              if (key === "expand") return expandAllBelow(r.key);
              if (key === "collapse") return toggleCollapsed(r.key);
            },
          }}>
            <Button size="small" type="text" icon={<MoreOutlined />} title="Move to section, move above / below a row, expand" />
          </Dropdown>
          <Button size="small" type="text" danger icon={<DeleteOutlined />}
                  onClick={() => setRows((rs) => removeRow(rs, r.key))} />
        </Space>
        );
      },
    },
  ];

  // Every column carries a stable key (its title), takes its width from
  // the remembered map when there is one, and gets a drag handle.
  const resizableColumns = columns.map((c, i) => {
    // A JSX title stringifies to "[object Object]", which would give
    // every tooltip-titled column the same key and one shared width.
    const key = c.key || c.dataIndex || (typeof c.title === "string" && c.title ? c.title : `col-${i}`);
    const width = colW[key] || c.width;
    return {
      ...c,
      width,
      ellipsis: c.ellipsis !== undefined ? c.ellipsis : false,
      onHeaderCell: () => ({ width, onResize: beginColResize(key, width) }),
    };
  });
  const tableWidth = resizableColumns.reduce((a, c) => a + (Number(c.width) || 0), 0);
  // Mount the horizontal scroll container ONLY when the columns really
  // need it. Passing scroll.x when it is smaller than the container makes
  // antd stretch the columns to fill exactly - and the table's own border
  // then pushes scrollWidth 4px past clientWidth, which is a scrollbar
  // for nothing. Once someone drags a column wide enough to overflow, the
  // container comes back and scrolling works as it should.
  const SCROLL_AT = 1900;
  // The table is VIRTUALISED, which needs a fixed body height.
  //
  // Without it antd mounts every row, and every row of this table is a
  // dozen live editors - selects, tree-selects, date inputs. Measured on
  // the production SpaceX project (138 activities): 553 text inputs,
  // 46,000 DOM nodes, 7,200 listeners, and a tab sitting at 620 MB after
  // one edit - which is what was running the browser out of memory while
  // people typed. Virtualised, only the visible rows are mounted.
  //
  // The height is the viewport less the toolbars above and the button
  // below, floored so a short window still shows something usable. It is
  // in LAYOUT pixels: the app renders under html{zoom:.8}, so
  // innerHeight/0.8 is the space actually available to lay out in.
  const bodyH = Math.max(360, Math.round((viewportH / 0.8) - 520));
  const tableScroll = { x: tableWidth > SCROLL_AT ? tableWidth : undefined, y: bodyH };

  return (
    // The schedule is a wide table by nature (18 columns). The old 1680
    // cap, not the screen, is what forced a horizontal scrollbar: the SPA
    // renders under html{zoom:.8}, so a 1920 screen offers ~2400 layout
    // px and the cap threw most of it away.
    <div style={{ padding: 24, fontFamily: "Inter, system-ui, sans-serif", maxWidth: 2000, margin: "0 auto" }}>
      <CoordinationBreadcrumbs
        items={[{ label: "Dashboard & Schedule", to: `/coordination?projectId=${projectId}` }]}
        current="Schedule"
      />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0 }}>Schedule{projectTitle ? ` — ${projectTitle}` : ""}</h2>
          <div style={{ color: "#888", fontSize: 13, marginTop: 2 }}>
            {loadedFromPlan
              ? "Editing the project's schedule — WBS, durations and predecessors drive the dates; assign partners, disciplines and areas per activity."
              : "Build the schedule: activities, durations, predecessors, partners — or start from a template."}
          </div>
          {/* HOW FAR THESE RECORDS HAVE CAUGHT UP WITH THE SITE, where he
              asked for it: "just after the Schedule - Project name editable
              field/slider with current status accuracy".
              It sits under the heading rather than in the toolbar row because
              it qualifies the whole page - every date below it is only as true
              as this number says. The control hides itself for a reader who
              may not change it and still shows them the sentence, which is the
              part they need. */}
          <ReportingCatchUp projectId={projectId} compact />
        </div>
        <span style={{ color: "#888", fontSize: 13 }}>Project start</span>
        <input type="date" value={projectStart}
               style={{ border: "1px solid #d9d9d9", borderRadius: 4, padding: "3px 8px" }}
               onChange={(e) => setProjectStart(e.target.value)} />
        {/* The project's default working week. Every row that has not
            picked its own calendar follows this, so changing it here
            re-dates the whole schedule at once. */}
        <span style={{ color: "#888", fontSize: 13 }}>Default calendar</span>
        <Select filterOption={matchOption} size="small" style={{ minWidth: 210 }}
                value={(calendars.find((c) => c.isDefault) || {})._id ? String(calendars.find((c) => c.isDefault)._id) : undefined}
                options={calendarOptions} optionFilterProp="label" showSearch
                disabled={!canEditPlan}
                onChange={async (v) => {
                  try {
                    await setDefaultCalendar(projectId, v);
                    reloadCalendars();
                    message.success("Default calendar changed - dates recomputed");
                  } catch (e) { message.error(e.message || "Could not change the default"); }
                }} />
        {/* Primavera round trip. Import reads the WBS, relationships,
            calendars and activity codes - not just the activity list. */}
        {/* Sharing the schedule the way a sheet or the areas page is
            shared. A recipient sees only their own company's rows, so
            this is safe to send to one trade without showing them the
            whole programme. */}
        <Button icon={<ShareAltOutlined />} onClick={() => setShareOpen(true)}>
          Share with a partner
        </Button>
        <input ref={xerInputRef} type="file" accept=".xer" style={{ display: "none" }} onChange={onXerPicked} />
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "in", label: "Import a P6 .xer file…", disabled: !canEditPlan },
              { key: "out", label: "Export this schedule as .xer" },
            ],
            onClick: async ({ key }) => {
              if (key === "in") return xerInputRef.current && xerInputRef.current.click();
              try {
                await downloadXer(projectId, `${(projectTitle || "schedule").replace(/[^A-Za-z0-9_-]+/g, "_")}.xer`);
              } catch (e) { message.error(e.message || "P6 export failed"); }
            },
          }}
        >
          <Button loading={p6Busy}>Primavera P6 <DownOutlined /></Button>
        </Dropdown>
        {/* Baselines. Health measures against the ACTIVE one, so this
            is not a report - it is what "behind" means on this project. */}
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "variance", label: baselines.some((b) => b.isActive) ? "Variance against the baseline…" : "No baseline taken yet", disabled: !baselines.some((b) => b.isActive) },
              { type: "divider" },
              { key: "take", label: "Take a baseline now…", disabled: !canEditPlan },
              ...(baselines.length
                ? [{ type: "divider" }, ...baselines.map((b) => ({
                    key: `use:${b._id}`,
                    label: `${b.isActive ? "● " : "○ "}${b.name}`,
                    disabled: !canEditPlan || b.isActive,
                  }))]
                : []),
            ],
            onClick: async ({ key }) => {
              if (key === "variance") return openVariance("active");
              if (key === "take") {
                setBaselineName(`Baseline ${new Date().toISOString().slice(0, 10)}`);
                return Modal.confirm({
                  title: "Take a schedule baseline",
                  content: (
                    <div style={{ paddingTop: 8 }}>
                      <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>
                        Freezes today's dates under a name. Health then measures "behind" against
                        this rather than against wherever the plan happens to be.
                      </div>
                      <Input defaultValue={`Baseline ${new Date().toISOString().slice(0, 10)}`}
                             onChange={(e) => setBaselineName(e.target.value)} />
                    </div>
                  ),
                  okText: "Take baseline",
                  onOk: async () => {
                    try {
                      const r = await takeBaseline(projectId, { name: baselineName || `Baseline ${new Date().toISOString().slice(0, 10)}` });
                      message.success(`Baseline "${r.name}" taken — ${r.activities} activities frozen`);
                      reloadBaselines();
                    } catch (e) { message.error(e.message || "Could not take that baseline"); }
                  },
                });
              }
              if (String(key).startsWith("use:")) {
                try {
                  const r = await activateBaseline(projectId, String(key).slice(4));
                  message.success(`Measuring against "${r.name}"`);
                  reloadBaselines();
                } catch (e) { message.error(e.message || "Could not switch baseline"); }
              }
            },
          }}
        >
          <Button>Baseline <DownOutlined /></Button>
        </Dropdown>
        <Button onClick={() => setHolidayModal(true)}>
          Holidays ({holidays.length})
        </Button>
        <Button icon={<ThunderboltOutlined />} onClick={() => setTemplateModalOpen(true)} disabled={!can.ganttAdd}
                title={can.ganttAdd ? "Insert activities from a template" : "Needs the ganttAdd permission"}>
          Templates
        </Button>
        <Button onClick={() => setSystemTasksOpen(true)} disabled={!(can.ganttAdd && can.ganttEdit)}
                data-testid="open-system-tasks"
                title={can.ganttAdd && can.ganttEdit ? "Add a task per system under every area, from Levels, Areas & Systems" : "Needs the ganttAdd and ganttEdit permissions"}>
          Add system tasks
        </Button>
        <Button onClick={() => setSimulateOpen(true)} disabled={!can.ganttEdit}
                data-testid="open-simulate"
                title={can.ganttEdit ? "Read registers, trackers, minutes and drawings and propose the schedule from them, on the levels, areas and lines of this project" : "Needs the ganttEdit permission"}>
          Simulate a schedule
        </Button>
        {existingItems.length > 0 && (
          <Button onClick={() => loadCurrentPlan()} title="Discard unsaved edits and reload the saved schedule into the editor">
            {loadedFromPlan ? "Reset to saved" : `Load current schedule (${existingItems.length})`}
          </Button>
        )}
        <span style={{ flex: 1 }} />
        {can.ganttAdd && (
          <Button icon={<FileSearchOutlined />} onClick={() => history.push(`/schedule-import/${projectId}`)}
                  title="Import PDF meeting minutes, CSV or Excel trackers into this schedule (activities, milestones, issues, checklists, minutes) with a confirmed field mapping">
            Import documents
          </Button>
        )}
        {(can.ganttEdit || can.ganttAdd) && (
          <Button icon={<UsergroupAddOutlined />} onClick={() => setContractorModal(true)}
                  title="Map contractor shorthand from imported documents (BES, DP, ...) to companies and people on this project">
            Map contractors
          </Button>
        )}
        <Button icon={<TeamOutlined />} onClick={() => history.push(`/partners?projectId=${projectId}`)}>Partners</Button>
        <Button
          onClick={() => history.push(`/areas/${projectId}`)}
          title="Levels & Areas - the levels and zones this job is organised by, one page per level on its own drawing. The schedule's levels and areas come from here."
        >
          Levels &amp; Areas
          {pendingAreas > 0 && (
            <Badge
              count={pendingAreas}
              style={{ backgroundColor: "#faad14", marginLeft: 6 }}
              title={`${pendingAreas} area(s) read off a sheet are waiting for you to validate`}
            />
          )}
        </Button>
        <Dropdown trigger={["click"]} menu={{ items: [
          { key: "gantt", label: "Gantt chart - wide sheet, every column + timeline", onClick: () => window.open(`/gantt-print/${projectId}`, "_blank") },
          { key: "report", label: "Schedule report - activities with issues, checklists, relationships", onClick: () => window.open(`/schedule-print/${projectId}`, "_blank") },
        ] }}>
          <Button icon={<PrinterOutlined />}
                  title="Print-ready PDF: the full Gantt chart on one wide sheet, or the detailed schedule report; pick a contractor on the page for their portion only">
            Print / PDF <DownOutlined />
          </Button>
        </Dropdown>
        {/* Saving creates new rows (ganttAdd), re-parents / re-links
            existing ones (ganttEdit) and drops removed ones (ganttDelete). */}
        <Popconfirm title={loadedFromPlan
            ? `Save changes to the schedule (${computed.rows.filter((r) => r.name.trim() && r.endDate).length} activities)?`
            : `Create a schedule with ${computed.rows.filter((r) => r.name.trim() && r.endDate).length} activit${computed.rows.filter((r) => r.name.trim() && r.endDate).length === 1 ? "y" : "ies"}?`} onConfirm={save}
            disabled={!(loadedFromPlan ? can.ganttEdit : can.ganttAdd) || !calendarsReady}>
          <Button type="primary" icon={<SaveOutlined />} loading={saving || !calendarsReady}
                  disabled={!(loadedFromPlan ? can.ganttEdit : can.ganttAdd) || !calendarsReady}
                  title={!calendarsReady ? "Loading calendars and holidays…" : (loadedFromPlan ? can.ganttEdit : can.ganttAdd) ? "" : `Needs the ${loadedFromPlan ? "ganttEdit" : "ganttAdd"} permission`}>
            {saveProgress ? `Saving ${saveProgress.done} of ${saveProgress.total}…` : "Save schedule"}
          </Button>
        </Popconfirm>
      </div>

      <Alert type="info" showIcon style={{ marginBottom: 12 }}
             message="Indent rows to nest them under a summary; set durations and finish-to-start predecessors by row number (e.g. 2,3) - a link follows the row it points at, so moving or inserting rows keeps it - and dates auto-schedule from the project start. Durations count working days (Mon–Fri) and skip weekends and company holidays; milestones, sign-offs and area releases are single dates. Drag bars in the chart below to move / resize activities in the draft." />

      {computed.errors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
               message={computed.errors.join("; ")} />
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <Input.Search allowClear placeholder="Search activities (name, WBS, type, discipline, area, contractor / trade, person…)" value={search}
                      onChange={(e) => setSearch(e.target.value)} style={{ width: 380 }} />
        <Button type={todayOnly ? "primary" : "default"} onClick={() => setTodayOnly((v) => !v)} title="Rows running today (or overdue and not done)">Today</Button>
        <DateRangeFilter
          label="Starts"
          value={startRange}
          onChange={setStartRange}
          title="Activities whose computed start falls between these two days, inclusive. Leave either end empty for an open window."
        />
        <DateRangeFilter
          label="Finishes"
          value={endRange}
          onChange={setEndRange}
          title="Activities whose computed finish falls between these two days, inclusive."
        />
        <Button onClick={() => { setAssignOpen(true); setAssignCompany(undefined); setAssignPeople([]); }} disabled={!visibleRows.length}
                title="Apply a partner / responsible people to every row shown (tasks and milestones), then Save schedule">
          Assign responsible ({visibleRows.length})
        </Button>
        {(query || todayOnly || !rangeEmpty(startRange) || !rangeEmpty(endRange)) && (
          <>
            <Button type="text" onClick={() => {
              setSearch(""); setTodayOnly(false);
              setStartRange({ from: "", to: "" }); setEndRange({ from: "", to: "" });
            }}>Clear filters</Button>
            <span style={{ color: "#888", fontSize: 12 }}>
              {visibleRows.length} of {rows.length} row{rows.length === 1 ? "" : "s"} match — row numbers stay those of the full schedule
            </span>
          </>
        )}
      </div>
      {/* Sections: fold the table to a depth, and act on a selection. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        <span style={{ color: "#888", fontSize: 12 }}>Sections</span>
        <Button size="small" onClick={() => collapseTo(0)} disabled={filtersOn}
                title="Show only the top-level heads">Collapse all</Button>
        {[1, 2, 3].map((lvl) => (
          <Button key={lvl} size="small" onClick={() => collapseTo(lvl)} disabled={filtersOn}
                  title={`Show rows down to indent level ${lvl} (heads are level 0)`}>
            Expand to level {lvl}
          </Button>
        ))}
        <Button size="small" onClick={() => setCollapsed(new Set())} disabled={filtersOn}>Expand all</Button>
        {filtersOn ? <span style={{ color: "#888", fontSize: 12 }}>(folding is off while filtering)</span> : null}
        {!filtersOn && visibleRows.length < rows.length ? (
          <span style={{ color: "#888", fontSize: 12 }}>{visibleRows.length} of {rows.length} rows shown</span>
        ) : null}
        <span style={{ flex: 1 }} />
        {selectedKeys.length > 0 && (
          <Space size={4} wrap style={{ background: "#e6f4ff", border: "1px solid #91caff", borderRadius: 6, padding: "2px 8px" }}>
            <b style={{ fontSize: 12 }}>{selectedKeys.length} selected</b>
            <Button size="small" onClick={() => openMove(selectedKeys, "section")}>Move to section…</Button>
            <Button size="small" onClick={() => openMove(selectedKeys, "relative")}>Move above / below…</Button>
            <Dropdown trigger={["click"]} menu={{
              items: [
                { key: "area", label: "Set area…" },
                { key: "phase", label: "Set phase…" },
                { key: "step", label: "Set step…" },
                { key: "package", label: "Set package…" },
                { key: "responsible", label: "Set responsible company…" },
                { key: "discipline", label: "Set discipline…" },
                { key: "level", label: "Set level…" },
                { key: "grid", label: "Set grid…" },
                { key: "type", label: "Set type…" },
                { key: "name", label: "Set name…" },
                { key: "status", label: "Set status…" },
                { key: "signoff", label: "Set sign-off date…" },
                { type: "divider" },
                { key: "shift", label: "Shift dates by N working days…" },
              ],
              onClick: ({ key }) => openBulk(key),
            }}>
              <Button size="small">Set… <DownOutlined /></Button>
            </Dropdown>
            {selectedKeys.length === 1 ? (
              <Tooltip title="Divide this group's period equally between its children in WORKING days, and each child between its own, down to the leaves. This is the one place a group may finish earlier than the work inside it.">
                <Button size="small" icon={<ColumnWidthOutlined />} onClick={openRedistribute}>
                  Redistribute
                </Button>
              </Tooltip>
            ) : null}
            {redistUndo ? (
              <Tooltip title="Put every date back as it was before the last redistribute. Only the last one.">
                <Button size="small" onClick={() => { setRows(redistUndo); setRedistUndo(null); message.success("Dates restored"); }}>
                  Undo redistribute
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip title="Copy the selected rows and everything under them. The copies carry the shape - dates, discipline, level, area - but no progress, status or sign-off.">
              <Button size="small" icon={<CopyOutlined />} onClick={bulkDuplicate}>Duplicate with children</Button>
            </Tooltip>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={bulkDelete}>Delete</Button>
            <Button size="small" type="link" onClick={() => setSelectedKeys([])}>Clear selection</Button>
          </Space>
        )}
      </div>
      {/* tableLayout="fixed" is what makes the widths above MEAN
          something: without it antd treats `width` as a hint and lets a
          content-heavy cell (every one of these columns holds a select)
          push its column wider - which is how 1856px of columns rendered
          as 2245px and kept the horizontal scrollbar. */}
      <Table rowKey="key" dataSource={visibleRows} columns={resizableColumns}
             size="small" pagination={false} tableLayout="fixed"
             virtual
             scroll={tableScroll}
             rowSelection={{
               selectedRowKeys: selectedKeys,
               // Rows folded away or filtered out stay selected.
               preserveSelectedRowKeys: true,
               columnWidth: 32,
               onChange: (keys) => setSelectedKeys(keys),
             }}
             components={{ header: { cell: ResizableTitle } }}
             rowClassName={(r) => `${r.key === focusRowKey ? "schedule-row-focus" : ""}${r.disabled ? " schedule-row-disabled" : ""}`}
             onRow={(r) => (r.key === focusRowKey ? { style: { background: "#fffbe6", boxShadow: "inset 3px 0 0 #faad14" } } : (r.disabled ? { style: { opacity: 0.6, background: "#fafafa" } } : {}))}
             locale={{ emptyText: query ? `No activities match "${search.trim()}"` : "No activities yet" }} />
      <Button icon={<PlusOutlined />} style={{ marginTop: 8 }}
              onClick={() => setRows((rs) => [...rs, newRow()])}>
        Add activity
      </Button>

      <ShareResourceModal
        type="schedule"
        id={projectId}
        projectId={projectId}
        title={`${projectTitle || "Project"} - schedule`}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />

      <ContractorMappingModal open={contractorModal} onClose={() => setContractorModal(false)} projectId={projectId}
        canEdit={!!(can.ganttEdit || can.ganttAdd)}
        onChanged={() => { getGanttFeed(projectId).then((res) => { const items = res?.items || []; setExistingItems(items); if (items.length) loadCurrentPlan(items); }).catch(() => {}); }} />
      <Modal title={`Assign responsible - ${visibleRows.length} row(s) shown`} open={assignOpen} onCancel={() => setAssignOpen(false)}
        okText="Apply to shown rows"
        onOk={() => {
          if (!assignCompany && !assignPeople.length) return message.warning("Pick a partner and/or people");
          const keys = new Set(visibleRows.map((r) => r.key));
          const group = roster.find((g) => String(g.companyId) === String(assignCompany));
          const opts = schedulePeopleOf(group);
          const picked = assignPeople.map((v) => ({ value: v, name: (opts.find((o) => o.value === v) || {}).name || "" }));
          setRows((rs) => rs.map((r) => {
            if (!keys.has(r.key)) return r;
            const next = { ...r };
            if (assignCompany) { next.responsibleCompanyId = assignCompany; next.contractorLabel = ""; }
            if (picked.length) {
              const existing = assignReplace ? [] : (r.responsibleUsers || []);
              const merged = [...existing];
              for (const p of picked) if (!merged.some((m) => m.value === p.value)) merged.push(p);
              next.responsibleUsers = merged; next.responsibleUserId = merged[0]?.value;
            } else if (assignCompany && String(r.responsibleCompanyId) !== String(assignCompany)) {
              next.responsibleUsers = []; next.responsibleUserId = undefined;
            }
            return next;
          }));
          message.success(`Applied to ${keys.size} row(s) - Save schedule to persist`);
          setAssignOpen(false);
        }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>Partner / contractor</div>
          <Select filterOption={matchOption} allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} placeholder="Partner company" value={assignCompany}
                  options={companyOptions} onChange={(v) => { setAssignCompany(v); setAssignPeople([]); }} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>Responsible people (one or many)</div>
          <Select filterOption={matchOption} mode="multiple" allowClear showSearch style={{ width: "100%" }} disabled={!assignCompany}
                  placeholder={assignCompany ? "People (contacts without an account get one on save)" : "Pick the partner first"} value={assignPeople}
                  options={schedulePeopleOf(roster.find((g) => String(g.companyId) === String(assignCompany)))}
                  optionFilterProp="search" optionRender={renderPeopleOption}
                  onChange={setAssignPeople} />
        </div>
        <label style={{ fontSize: 12 }}><input type="checkbox" checked={assignReplace} onChange={(e) => setAssignReplace(e.target.checked)} style={{ marginRight: 6 }} />Replace the rows' current people (otherwise added)</label>
      </Modal>
      <Modal
        title={moveDlg
          ? `${moveDlg.mode === "section" ? "Move to section" : "Move above / below a row"} - ${moveDlg.keys.length === 1
            ? `"${(rows.find((x) => x.key === moveDlg.keys[0]) || {}).name || "row"}"`
            : `${moveDlg.keys.length} rows`}`
          : ""}
        open={!!moveDlg}
        onCancel={() => setMoveDlg(null)}
        onOk={applyMove}
        okText="Move"
        width={640}
        destroyOnClose
      >
        <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
          Each row moves with everything under it. Links, dates and saved ids go with it; Save schedule to persist.
        </div>
        {moveDlg && moveDlg.mode === "section" ? (
          <>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Section - the rows become its last children</div>
            <TreeSelect style={{ width: "100%" }} showSearch allowClear treeNodeFilterProp="title"
                        placeholder="Search sections (BIM / Field / Management, areas, zones, packages…)"
                        value={moveTarget} onChange={setMoveTarget}
                        treeData={sectionTree} treeDefaultExpandAll={false}
                        dropdownStyle={{ maxHeight: 460, overflow: "auto" }} listHeight={420} />
          </>
        ) : moveDlg ? (
          <>
            <Radio.Group value={moveWhere} onChange={(e) => setMoveWhere(e.target.value)} style={{ marginBottom: 8 }}>
              <Radio value="above">Above the row</Radio>
              <Radio value="below">Below the row (after everything under it)</Radio>
            </Radio.Group>
            <Select filterOption={matchOption} style={{ width: "100%" }} showSearch allowClear optionFilterProp="label"
                    placeholder="Search rows by number, WBS or name"
                    value={moveTarget} onChange={setMoveTarget} options={rowPickOptions}
                    listHeight={420} />
            <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>The moved rows take that row's indent.</div>
          </>
        ) : null}
      </Modal>
      <Modal
        title={bulkDlg ? `${{
          area: "Set area", phase: "Set phase", step: "Set step", package: "Set package",
          responsible: "Set responsible company", shift: "Shift dates",
          discipline: "Set discipline", level: "Set level", grid: "Set grid",
          type: "Set type", name: "Set name",
          status: "Set status", signoff: "Set sign-off date",
        }[bulkDlg.kind]} - ${selectedKeys.length} selected row${selectedKeys.length === 1 ? "" : "s"}` : ""}
        open={!!bulkDlg}
        onCancel={() => setBulkDlg(null)}
        onOk={applyBulk}
        okText="Apply"
        destroyOnClose
      >
        {bulkDlg && bulkDlg.kind === "area" && laOn && (
          <Select filterOption={matchOption} style={{ width: "100%" }} allowClear showSearch optionFilterProp="label"
                  placeholder="Area from Levels & Areas (empty clears it)" value={bulkValue} onChange={setBulkValue} options={laAreaOptions} />
        )}
        {bulkDlg && bulkDlg.kind === "area" && !laOn && (
          <TreeSelect style={{ width: "100%" }} allowClear showSearch treeDefaultExpandAll treeNodeFilterProp="title"
                      placeholder="Area (empty clears it)" value={bulkValue} onChange={setBulkValue} treeData={areaTree} />
        )}
        {bulkDlg && bulkDlg.kind === "phase" && (
          <Select style={{ width: "100%" }} allowClear placeholder="Phase (empty clears it)" value={bulkValue} onChange={setBulkValue}
                  options={[{ value: "bim", label: "BIM" }, { value: "field", label: "Field" }, { value: "management", label: "Management" }]} />
        )}
        {bulkDlg && bulkDlg.kind === "step" && (
          <Select filterOption={matchOption} style={{ width: "100%" }} allowClear showSearch mode="tags" maxTagCount={1} placeholder="Step (type to use another; empty clears it)"
                  value={bulkValue ? [bulkValue] : []}
                  onChange={(v) => setBulkValue(v && v.length ? String(v[v.length - 1]) : undefined)}
                  options={SCHEDULE_STEPS.map((v) => ({ value: v, label: stepLabel(v) }))} />
        )}
        {bulkDlg && bulkDlg.kind === "package" && (
          <Input placeholder="Work package code, e.g. ELE-CT-L1-D (empty clears it)" value={bulkValue || ""}
                 onChange={(e) => setBulkValue(e.target.value.toUpperCase())} />
        )}
        {bulkDlg && bulkDlg.kind === "responsible" && (
          <>
            <Select filterOption={matchOption} style={{ width: "100%" }} allowClear showSearch optionFilterProp="label"
                    placeholder="Partner company (empty clears it)" value={bulkValue} onChange={setBulkValue} options={companyOptions} />
            <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
              Summary rows are skipped. Changing the company clears the rows' responsible people - use Assign responsible to set people.
            </div>
          </>
        )}
        {bulkDlg && bulkDlg.kind === "discipline" && (
          <Select filterOption={matchOption} style={{ width: "100%" }} allowClear showSearch optionFilterProp="text" optionLabelProp="name"
                  placeholder="Discipline (empty clears it)" value={bulkValue} onChange={setBulkValue}
                  options={disciplineOptionsWithPartners(disciplines, tradeIx)} />
        )}
        {bulkDlg && bulkDlg.kind === "level" && laOn && (
          <Select filterOption={matchOption} style={{ width: "100%" }} allowClear showSearch placeholder="Level from Levels & Areas (empty clears it)"
                  value={bulkValue} onChange={setBulkValue} options={laLevelOptions} />
        )}
        {bulkDlg && bulkDlg.kind === "level" && !laOn && (
          <Input placeholder="Level, e.g. L1 (empty clears it)" value={bulkValue || ""}
                 onChange={(e) => setBulkValue(e.target.value)} />
        )}
        {bulkDlg && bulkDlg.kind === "grid" && (
          <Input placeholder="Grid, e.g. K/57 (empty clears it)" value={bulkValue || ""}
                 onChange={(e) => setBulkValue(e.target.value)} />
        )}
        {bulkDlg && bulkDlg.kind === "type" && (
          <>
            <Select filterOption={matchOption} style={{ width: "100%" }} showSearch optionFilterProp="label"
                    placeholder="Activity type" value={bulkValue} onChange={setBulkValue}
                    options={ACTIVITY_TYPES.filter((t) => t.value !== "summary").map((t) => ({ value: t.value, label: t.label }))} />
            <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
              Summary rows are skipped - their dates come from the rows under them. A milestone type sets the
              duration to zero, exactly as the row picker does.
            </div>
          </>
        )}
        {bulkDlg && bulkDlg.kind === "name" && (
          <>
            <Input placeholder="New name for every selected row" value={bulkValue || ""}
                   onChange={(e) => setBulkValue(e.target.value)} />
            <div style={{ fontSize: 12, color: "#8a6d3b", marginTop: 6 }}>
              This gives all {selectedKeys.length} selected rows the SAME name. It is rarely what you want on
              more than a few - they will be told apart only by their place in the tree.
            </div>
          </>
        )}
        {bulkDlg && bulkDlg.kind === "status" && (
          <>
            <Select style={{ width: "100%" }} placeholder="Status" value={bulkValue}
                    onChange={setBulkValue} options={STATUS_OPTIONS} />
            <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
              Done also completes the work hanging off each row - its issues are closed and its checklist
              items completed. Disabled rows are skipped, and the result says how many.
            </div>
          </>
        )}
        {bulkDlg && bulkDlg.kind === "signoff" && (
          <>
            <input type="date" value={bulkValue || ""} onChange={(e) => setBulkValue(e.target.value)}
                   style={{ border: "1px solid #d9d9d9", borderRadius: 4, padding: "4px 8px", fontSize: 13, width: "100%" }} />
            <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
              Empty clears the date, which means the activity needs no sign-off. Rows ALREADY SIGNED OFF are
              skipped - the date is what was signed, so withdraw the sign-off first to change it - and so are
              disabled rows. The result says how many of each.
            </div>
          </>
        )}
        {bulkDlg && bulkDlg.kind === "shift" && (
          <>
            <Space>
              <InputNumber value={bulkValue} onChange={setBulkValue} min={-999} max={999} />
              <span>working days (negative = earlier)</span>
            </Space>
            <div style={{ marginTop: 8 }}>
              <Checkbox checked={bulkShiftSignOff} onChange={(e) => setBulkShiftSignOff(e.target.checked)}>
                Also shift sign-off dates (not ones already signed off)
              </Checkbox>
            </div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
              Pins each activity's start (a selected summary shifts everything under it), exactly like typing a new Start.
              Durations are kept and the schedule recomputes; a row tied to predecessors cannot start before its links allow.
            </div>
          </>
        )}
      </Modal>
      <Modal
        title={splitDlg ? `Split period across ${splitDlg.segments.length} child${splitDlg.segments.length === 1 ? "" : "ren"}` : "Split period"}
        open={!!splitDlg}
        onCancel={() => setSplitDlg(null)}
        onOk={applySplit}
        okText="Apply these dates"
        destroyOnClose
      >
        {splitDlg && (
          <>
            <div style={{ fontSize: 12.5, color: "#444", marginBottom: 8 }}>
              Each child starts on the day the one before it ends, the first starts with the parent and the last
              finishes on the parent&apos;s final day. Days are CALENDAR days, so a child can start on a weekend.
            </div>
            <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#888" }}>
                  <th style={{ padding: "2px 6px" }}>Activity</th>
                  <th style={{ padding: "2px 6px" }}>Start</th>
                  <th style={{ padding: "2px 6px" }}>Finish</th>
                  <th style={{ padding: "2px 6px" }}>Days</th>
                </tr>
              </thead>
              <tbody>
                {splitDlg.segments.map((sg, i) => {
                  const row = rows.find((r) => r.key === sg.key);
                  const last = i === splitDlg.segments.length - 1;
                  const uneven = last && splitDlg.segments.length > 1
                    && sg.days !== splitDlg.segments[0].days;
                  return (
                    <tr key={sg.key}>
                      <td style={{ padding: "2px 6px" }}>{row ? row.name || "(unnamed)" : sg.key}</td>
                      <td style={{ padding: "2px 6px" }}>{sg.start}</td>
                      <td style={{ padding: "2px 6px" }}>{sg.end}</td>
                      <td style={{ padding: "2px 6px" }}>
                        {sg.days}
                        {uneven ? <span style={{ color: "#8a6d3b" }}> (takes the remainder)</span> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: 12, color: "#888", marginTop: 10 }}>
              Only these direct children move - nothing deeper. Durations are stored in working days, so a child
              whose slice covers a weekend shows fewer than one of the same calendar length. Baselines and
              forecasts are left alone, and a child tied to predecessors cannot start before its links allow.
            </div>
          </>
        )}
      </Modal>
      <Modal
        title={variance ? `Variance against "${variance.baseline.name}"` : "Variance"}
        open={varianceOpen}
        onCancel={() => setVarianceOpen(false)}
        footer={null}
        width={860}
        destroyOnClose
      >
        {variance && (
          <div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
              Baseline taken {new Date(variance.baseline.takenAt).toLocaleDateString()} ·
              slip is measured on the FINISH date, in whole days.
            </div>
            <Space wrap size={16} style={{ marginBottom: 14 }}>
              <Tag color={variance.summary.late ? "red" : "green"}>
                {variance.summary.late} late
              </Tag>
              <Tag color="green">{variance.summary.early} early</Tag>
              <Tag>{variance.summary.onTime} on the baseline</Tag>
              {variance.summary.worstSlipDays > 0 && (
                <Tag color="red">worst: {slipLabel(variance.summary.worstSlipDays)}</Tag>
              )}
              {variance.summary.late > 0 && (
                <span style={{ fontSize: 12, color: "#888" }}>
                  average of the late ones: {variance.summary.averageLateDays} days
                </span>
              )}
              {/* Work that arrived or left since the baseline is counted
                  separately - folding it into the slip would report a
                  number about work nobody had promised. */}
              {!!variance.summary.added && <Tag color="blue">{variance.summary.added} added since</Tag>}
              {!!variance.summary.removed && <Tag>{variance.summary.removed} removed since</Tag>}
            </Space>
            {[["byArea", "Area"], ["byDiscipline", "Discipline"], ["byStage", "Stage"]].map(([k, label]) => (
              (variance[k] || []).length ? (
                <div key={k} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>By {label.toLowerCase()}</div>
                  <Table size="small" pagination={false} rowKey="key"
                         dataSource={variance[k]}
                         columns={[
                           { title: label, dataIndex: "label" },
                           { title: "Activities", dataIndex: "activities", width: 90 },
                           { title: "Late", dataIndex: "late", width: 70 },
                           {
                             title: "Worst", width: 150,
                             render: (_, r) => (
                               <span style={{ color: r.worstSlipDays > 0 ? "#d4380d" : "#389e0d" }}>
                                 {slipLabel(r.worstSlipDays)}
                               </span>
                             ),
                           },
                         ]} />
                </div>
              ) : null
            ))}
          </div>
        )}
      </Modal>
      <Modal
        title="Company holiday calendar"
        open={holidayModal}
        onCancel={() => setHolidayModal(false)}
        footer={null}
        destroyOnClose
      >
        <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
          Non-working days for every schedule in your company — durations
          skip them like weekends.{!canEditHolidays && " Ask a company admin to change this list."}
        </div>
        {holidays.length === 0 ? (
          <Tag style={{ marginBottom: 10 }}>No holidays defined</Tag>
        ) : (
          holidays
            .slice()
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((h) => (
              <div key={h.date}
                   style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderBottom: "1px solid #f5f5f5" }}>
                <b style={{ width: 100 }}>{h.date}</b>
                <span style={{ flex: 1, color: "#666" }}>{h.label || "—"}</span>
                {canEditHolidays && (
                  <Button size="small" type="text" danger
                          onClick={() => saveHolidays(holidays.filter((x) => x.date !== h.date))}>
                    remove
                  </Button>
                )}
              </div>
            ))
        )}
        {canEditHolidays && (
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input type="date" value={newHoliday.date}
                   style={{ border: "1px solid #d9d9d9", borderRadius: 4, padding: "3px 8px" }}
                   onChange={(e) => setNewHoliday((h) => ({ ...h, date: e.target.value }))} />
            <Input placeholder="Label (e.g. Labor Day)" style={{ flex: 1 }}
                   value={newHoliday.label}
                   onChange={(e) => setNewHoliday((h) => ({ ...h, label: e.target.value }))} />
            <Button type="primary" disabled={!newHoliday.date}
                    onClick={() => {
                      saveHolidays([
                        ...holidays.filter((x) => x.date !== newHoliday.date),
                        { date: newHoliday.date, label: newHoliday.label },
                      ]);
                      setNewHoliday({ date: "", label: "" });
                    }}>
              Add
            </Button>
          </div>
        )}
      </Modal>

      <Card size="small" title="Schedule chart (draft) — drag a bar to move it, drag either end to change its start or finish, drag a name in the list to move the row, right-click an activity for more; Save schedule to persist" style={{ marginTop: 16 }}>
        {previewItems.length ? (
          <CoordinationGantt items={chartItems} editable onLocalChange={onGanttLocalChange} onContextAction={onGanttContext} onMoveRow={onGanttMoveRow} companyNames={companyNamesById} filterText={search} todayOnly={todayOnly} areasByItem={areasByItem} />
        ) : (
          <Tag>Name some activities to see the chart</Tag>
        )}
      </Card>

      <Modal title="Rename activity" open={!!ganttRename} onCancel={() => setGanttRename(null)} onOk={applyGanttRename} okText="Rename" destroyOnClose>
        <Input autoFocus value={ganttRename ? ganttRename.name : ""} onPressEnter={applyGanttRename}
               onChange={(e) => { const v = e.target.value; setGanttRename((g) => (g ? { ...g, name: v } : g)); }} />
      </Modal>
      <Modal title={ganttPlace ? `Place "${(rows.find((x) => x.key === ganttPlace.key) || {}).name || ""}" ${ganttPlace.mode === "before" ? "before" : "after"}…` : ""}
             open={!!ganttPlace} onCancel={() => setGanttPlace(null)} onOk={applyGanttPlace} okText="Place" width={640} destroyOnClose>
        <Select filterOption={matchOption} style={{ width: "100%" }} showSearch allowClear optionFilterProp="label" autoFocus
                placeholder="Search activities by WBS or name" listHeight={420}
                value={ganttPlace ? ganttPlace.target : undefined}
                onChange={(v) => setGanttPlace((g) => (g ? { ...g, target: v } : g))} options={placeOptions} />
        <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
          {ganttPlace && ganttPlace.mode === "before"
            ? "The chosen activity will start when this one finishes (a finish-to-start link)."
            : "This activity will start when the chosen one finishes (a finish-to-start link); its pinned start is released so it moves there."}
        </div>
      </Modal>
      <Modal
        title="Insert activities from a template"
        open={templateModalOpen}
        onCancel={() => setTemplateModalOpen(false)}
        onOk={applyTemplate}
        okText={SCHEDULE_TEMPLATES.find((t) => t.key === templateKey)?.serverBuild ? "Check and preview" : "Add activities"}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 8 }}>
          <div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Template</div>
            <Select value={templateKey} style={{ width: "100%" }}
                    options={SCHEDULE_TEMPLATES.map((t) => ({ value: t.key, label: t.name }))}
                    onChange={(val) => setTemplateKey(val)} />
            <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
              {SCHEDULE_TEMPLATES.find((t) => t.key === templateKey)?.description}
            </div>
          </div>
          {SCHEDULE_TEMPLATES.find((t) => t.key === templateKey)?.areaAware && (
            <div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                Areas — the sequence is repeated once per area, under a summary each. Leave empty to add it once.
              </div>
              <Select mode="multiple" allowClear style={{ width: "100%" }} placeholder="No areas (add the sequence once)"
                      value={templateAreas} onChange={setTemplateAreas} optionFilterProp="label"
                      options={(areas || []).map((a) => ({ value: String(a._id), label: `${a.code ? `${a.code} · ` : ""}${a.name}` }))} />
            </div>
          )}
          {!SCHEDULE_TEMPLATES.find((t) => t.key === templateKey)?.serverBuild && (<>
          <div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Anchor date — the first activity starts here; everything after it follows its links</div>
            <input type="date" value={anchorDate}
                   style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: "3px 8px", fontSize: 13, width: "100%" }}
                   onChange={(e) => setAnchorDate(e.target.value)} />
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>
            Activities are appended to the draft above — review, then "Save schedule".
          </div>
          </>)}
          {SCHEDULE_TEMPLATES.find((t) => t.key === templateKey)?.serverBuild && (
            <div style={{ fontSize: 12, color: "#888" }}>
              The levels, areas and systems are checked first; the result is saved straight to the schedule after you see
              the preview, and the editor reloads it (unsaved edits on this page are replaced). It can be undone.
            </div>
          )}
        </div>
      </Modal>
      <ScheduleSimulator
        projectId={projectId}
        open={simulateOpen}
        onClose={() => setSimulateOpen(false)}
        onApplied={() => { getGanttFeed(projectId).then((res) => { const items = res?.items || []; setExistingItems(items); setAreasByItem(res?.areasByItem || {}); if (items.length) loadCurrentPlan(items); }).catch(() => {}); }}
      />
      <SystemTasksBuilder
        projectId={projectId}
        open={systemTasksOpen}
        onClose={() => setSystemTasksOpen(false)}
        onApplied={() => { getGanttFeed(projectId).then((res) => { const items = res?.items || []; setExistingItems(items); setAreasByItem(res?.areasByItem || {}); if (items.length) loadCurrentPlan(items); }).catch(() => {}); }}
      />
      {/* Read-only: the field's own count for this activity, by category.
          Construction tracking is the field's lane (owner's rule); the
          plan's own percentage stays a separate claim. */}
      <FieldProgressByCategory
        projectId={projectId}
        activity={fieldProgressRow}
        canLink={canEditPlan}
        open={!!fieldProgressRow}
        onClose={() => setFieldProgressRow(null)}
      />
      {/* PREVIEW BEFORE IT COMMITS. He did not ask for this; it rewrites
          every date under a row in one click, on a live schedule. */}
      <Modal
        open={!!redist}
        title={`Redistribute "${redist ? redist.name : ""}"`}
        onCancel={() => setRedist(null)}
        onOk={applyRedistribute}
        okText={redist ? `Move ${redist.changes.length} activit${redist.changes.length === 1 ? "y" : "ies"}` : "Move"}
        destroyOnClose
      >
        {redist ? (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div>
              <b>{redist.span.workingDays}</b> working days,{" "}
              {redist.span.start} to {redist.span.end},
              divided between its children and theirs.
            </div>
            <div style={{ marginTop: 8 }}>
              <b>{redist.changes.length}</b> activit{redist.changes.length === 1 ? "y" : "ies"} will move.
              {redist.disabledMoved ? ` ${redist.disabledMoved} of them ${redist.disabledMoved === 1 ? "is" : "are"} disabled.` : ""}
            </div>
            {redist.refused.length ? (
              <div style={{ marginTop: 10, color: "#B45309" }}>
                {/* SAID, NOT SKIPPED. A node left alone without a word
                    looks like the button half-working. */}
                {redist.refused.length} group{redist.refused.length === 1 ? "" : "s"} left exactly as{" "}
                {redist.refused.length === 1 ? "it is" : "they are"} - too short to divide:
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {redist.refused.slice(0, 6).map((x) => (
                    <li key={x.key}>
                      <b>{x.name || "(unnamed)"}</b>: {x.workingDays} working day
                      {x.workingDays === 1 ? "" : "s"} for {x.children} children
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div style={{ marginTop: 10, color: "#6B7280", fontSize: 12.5 }}>
              Dates win: predecessor links stay on the rows, so a linked chain can read out of
              sequence until something moves again. Nothing is saved until Save schedule, and one
              Undo is offered afterwards.
            </div>
          </div>
        ) : null}
      </Modal>

      {/* MOVE THE DATES OF A BRANCH. Keyed on the row so opening a second
          activity re-previews rather than showing the first one's rows
          under the second one's name. It writes through its own gated
          route and immediately - unlike the draft editing on this page -
          so the feed is re-read afterwards rather than the draft patched,
          which would leave two notions of the dates. */}
      {offsetFor ? (
        <ScheduleOffsetDialog
          key={offsetFor._id}
          projectId={projectId}
          row={offsetFor}
          onClose={() => setOffsetFor(null)}
          onApplied={() => { refreshGates(); }}
        />
      ) : null}
    </div>
  );
}
